// AUTO-GENERATED from FAQ.docx — do not edit manually
// 8 modules | 549 FAQs total

export interface Faq {
  id: string;
  question: string;
  answer: string;
}

export interface SubModule {
  id: string;
  subModuleTitle: string;
  faqs: Faq[];
}

export interface FaqModule {
  id: string;
  moduleTitle: string;
  subModules: SubModule[];
}

export const faqData: FaqModule[] = [
  {
    "id": "module-1",
    "moduleTitle": "Onboarding",
    "subModules": [
      {
        "id": "module-1-sub-1",
        "subModuleTitle": "Overview & Progress",
        "faqs": [
          {
            "id": "module-1-sub-1-faq-1",
            "question": "What is the Marketing 4SIGHT onboarding flow?",
            "answer": "Onboarding is a streamlined 3-step process (plus a T&C acceptance step) that follows plan selection and payment. The three steps are: Step 1 — Business Info, Step 2 — Ecosystem (Audience & Competition), and Step 3 — Business Portfolio. The goal is to get you to your dashboard as quickly as possible with only the minimum required information captured upfront."
          },
          {
            "id": "module-1-sub-1-faq-2",
            "question": "How long does onboarding take?",
            "answer": "The 3-step flow is designed to be completed quickly. Only essential fields are captured — no complex integrations or keyword research are required at this stage. Most users should be able to complete onboarding in a few minutes."
          },
          {
            "id": "module-1-sub-1-faq-3",
            "question": "Does onboarding show a progress bar?",
            "answer": "Yes. A progress bar reflects the 3-step onboarding flow. Pre-gate steps (plan selection and payment on the website) are not counted in the progress bar — only the 3 core onboarding steps are tracked."
          },
          {
            "id": "module-1-sub-1-faq-4",
            "question": "Are there help icons and tooltips during onboarding?",
            "answer": "Yes. Help icons with tooltip videos are placed on every screen throughout onboarding, including the pre-gate steps (plan selection and payment). These provide contextual guidance without requiring you to leave the page."
          },
          {
            "id": "module-1-sub-1-faq-5",
            "question": "Will I lose my progress if I close the browser mid-onboarding?",
            "answer": "No. All onboarding steps support auto-save and resume functionality. If your session is interrupted — whether by a reload, browser close, or connection issue — your progress is preserved and you can continue from exactly where you left off."
          },
          {
            "id": "module-1-sub-1-faq-6",
            "question": "Is all my onboarding data saved permanently?",
            "answer": "Yes. Data collected during onboarding is permanently stored and pre-populated in your Profile section. This data must never be auto-cleared or overwritten by a failed save attempt."
          },
          {
            "id": "module-1-sub-1-faq-7",
            "question": "Do I need to re-enter onboarding data anywhere else in the platform?",
            "answer": "No. All information captured during onboarding persists and is reflected across all platform managers and dashboard views. Profile updates made post-onboarding also automatically refresh all connected dashboards without requiring re-entry."
          }
        ]
      },
      {
        "id": "module-1-sub-2",
        "subModuleTitle": "Step 1 — Business Info",
        "faqs": [
          {
            "id": "module-1-sub-2-faq-1",
            "question": "What fields are collected in Step 1?",
            "answer": "Step 1 — Business Info collects: Full Name (user's personal name for account personalisation), Username (auto-generated with an edit option), Dashboard Access Email (with OTP verification), Brand Name (your business's public-facing name), Website URL (optional), Brand Description, Business Type (Service / Product / Both), Primary Business Category, and whether the account is Managed by an Agency."
          },
          {
            "id": "module-1-sub-2-faq-2",
            "question": "Why is the username auto-generated?",
            "answer": "Auto-generation of the username is recommended to reduce friction during onboarding. You still have the option to edit the suggested username if you prefer a different one. This removes the need to think of and validate a unique username while setting up the account."
          },
          {
            "id": "module-1-sub-2-faq-3",
            "question": "What is the Dashboard Access Email used for?",
            "answer": "The Dashboard Access Email is the primary account email. It is used for: dashboard access, OTP verification during onboarding, delivery of login credentials, all platform notifications, reports, and invoices. It must be verified via OTP during Step 1."
          },
          {
            "id": "module-1-sub-2-faq-4",
            "question": "Is Website URL mandatory?",
            "answer": "No. Website URL is an optional field. If you do not have a website, you can still use the Content Manager and GMB Manager. However, the SEO Manager will be greyed out until a website URL is provided. You will be offered the option to use the Website Builder or raise a service request for website creation."
          },
          {
            "id": "module-1-sub-2-faq-5",
            "question": "What does the Website URL auto-validation check?",
            "answer": "The system validates that the URL is accessible and follows basic URL structure requirements (e.g., proper domain format). This ensures the URL is functional before it is used for SEO tracking and audit purposes."
          },
          {
            "id": "module-1-sub-2-faq-6",
            "question": "What is Brand Description used for?",
            "answer": "Brand Description is a foundational text field retained from the previous onboarding. It is used alongside the website URL for autofill purposes and to inform the platform's AI engines about your brand positioning and offering."
          },
          {
            "id": "module-1-sub-2-faq-7",
            "question": "What is Business Type and why does it matter?",
            "answer": "Business Type (Service / Product / Both) is  classification field introduced in the onboarding. It directly drives the SEO engine, GMB configuration, and content generation engines — allowing the platform to immediately tailor its recommendations to the nature of your business without requiring further configuration."
          },
          {
            "id": "module-1-sub-2-faq-8",
            "question": "What is Primary Business Category?",
            "answer": "Primary Business Category is a new field that drives the keyword intelligence engine and competitor mapping system. Selecting the right category allows the platform to initialise relevant keyword clusters and identify competitors in your space from the moment you complete onboarding."
          },
          {
            "id": "module-1-sub-2-faq-9",
            "question": "What is the 'Managed by an Agency' field?",
            "answer": "This field identifies whether your account is being set up and managed by a marketing agency on your behalf. When selected, post-onboarding routing logic differs — agency users are routed to the agency dashboard or the Add Brands screen based on their brand limit status (year-to-date)."
          },
          {
            "id": "module-1-sub-2-faq-10",
            "question": "Which fields in Step 1 are mandatory?",
            "answer": "The mandatory fields in Step 1 are: Full Name, Username, Dashboard Access Email (with OTP), Brand Name, Business Type, and Primary Business Category. Website URL and Brand Description are optional fields."
          }
        ]
      },
      {
        "id": "module-1-sub-3",
        "subModuleTitle": "Step 2 — Ecosystem",
        "faqs": [
          {
            "id": "module-1-sub-3-faq-1",
            "question": "What is the purpose of Step 2?",
            "answer": "Step 2 — Ecosystem defines your geographic scope and competitive landscape. It captures three core inputs: Location Scope (where your business operates), Customer Description (who your customers are), and Competitor Identification (who you compete with). These inputs power keyword classification, GMB routing logic, and competitor benchmarking from the moment onboarding is complete."
          },
          {
            "id": "module-1-sub-3-faq-2",
            "question": "QWhat location scope options can I select?",
            "answer": "You can select from three options: Local, National, or Global. The selection is made via radio buttons with an integrated autocomplete dropdown for Local selection. Multiple selections are possible — for example, you can select both Local and National if your business operates at both levels. This selection is used for SEO and GMB bifurcation logic."
          },
          {
            "id": "module-1-sub-3-faq-3",
            "question": "Can I type my location manually?",
            "answer": "No. Free-text entry is not permitted for the final location selection. You must use the typeahead autocomplete dropdown to select a valid, recognised location. This ensures clean, structured location data for the SEO and GMB engines."
          },
          {
            "id": "module-1-sub-3-faq-4",
            "question": "Why does location scope matter for the platform?",
            "answer": "Location scope determines how the keyword classification engine and GMB routing are calibrated. A Local business will have different keyword strategies and GMB priorities than a National or Global one. Selecting the correct scope from the start ensures your dashboard and recommendations are immediately relevant."
          },
          {
            "id": "module-1-sub-3-faq-5",
            "question": "What customer information is captured in Step 2?",
            "answer": "Customer Description has two sub-fields: (1) Audience Type — B2B, B2C, or Both (mandatory). (2) Persona Description — a free-text field for describing your ideal customer (optional). Age range, interests, profession, and gender were removed from Step 2 to reduce cognitive load; these can be refined post-onboarding in the SEO Manager."
          },
          {
            "id": "module-1-sub-3-faq-6",
            "question": "What is the 'Customer Description is manual only' rule?",
            "answer": "Step 2 requires all Customer Description fields to be filled in manually. Sub-fields include: Age, Interest / Profession, Locality / Gender, and free text. There is no auto-fill for this section."
          },
          {
            "id": "module-1-sub-3-faq-7",
            "question": "How do I add competitor information during onboarding?",
            "answer": "In Step 2, you can enter your competitor's Brand Name, Website URL, and Competition Insights (a free-text field for additional context)."
          },
          {
            "id": "module-1-sub-3-faq-8",
            "question": "Why is competitor information collected in Step 2?",
            "answer": "Step 2 captures your full market ecosystem — audience, location, and competition together — so the platform can immediately initialise keyword mapping and competitor benchmarking."
          },
          {
            "id": "module-1-sub-3-faq-9",
            "question": "What is the Physical Presence Indicator?",
            "answer": "It asks 'Do customers visit your location?' (Yes / No / Both) and determines whether the platform activates GMB routing and local keyword strategies for your account."
          },
          {
            "id": "module-1-sub-3-faq-10",
            "question": "Where is Search Intent configured?",
            "answer": "Search Intent is configured in the SEO Manager after onboarding, where you have full platform context to make informed intent decisions."
          }
        ]
      },
      {
        "id": "module-1-sub-4",
        "subModuleTitle": "Step 3 — Business Portfolio",
        "faqs": [
          {
            "id": "module-1-sub-4-faq-1",
            "question": "What does Step 3 capture?",
            "answer": "Products (name and URL), Services (name and URL), Key Page URLs, Brand Differentiators, Logo, Font, Colour Palette, and Social Media URLs (YouTube, Instagram, Facebook, LinkedIn)."
          },
          {
            "id": "module-1-sub-4-faq-2",
            "question": "Can I add multiple products and services?",
            "answer": "Yes. Both support multi-entry with add and remove functionality. Each entry includes a 'Mark as Priority' toggle."
          },
          {
            "id": "module-1-sub-4-faq-3",
            "question": "What does the 'Mark as Priority' toggle do?",
            "answer": "It flags your primary offerings so the platform prioritises them in keyword clustering and content recommendations."
          },
          {
            "id": "module-1-sub-4-faq-4",
            "question": "Are product and service URLs mandatory?",
            "answer": "No. They are optional. If you have no website, the SEO Manager will remain greyed out until a URL is added."
          },
          {
            "id": "module-1-sub-4-faq-5",
            "question": "What key page URLs should I enter?",
            "answer": "Home, Product/Service pages, Contact, About, Blog, and any custom or unique page URLs. All are optional and non-blocking."
          },
          {
            "id": "module-1-sub-4-faq-6",
            "question": "What are Brand Differentiators?",
            "answer": "Short-form inputs describing what makes your business unique — your competitive advantages and key value propositions."
          },
          {
            "id": "module-1-sub-4-faq-7",
            "question": "Is uploading a logo mandatory?",
            "answer": "No. Logo, Font, and Colour Palette are all optional and can be added or updated later in the Profile section."
          },
          {
            "id": "module-1-sub-4-faq-8",
            "question": "What logo formats are accepted?",
            "answer": "PNG, JPG, and SVG. A cropping and preview tool is shown before confirming the upload."
          },
          {
            "id": "module-1-sub-4-faq-9",
            "question": "What colour code formats are accepted?",
            "answer": "Hex, RGB, or HSL. Fonts must be from a predefined list or valid web-safe/system fonts. Invalid formats are rejected with an inline error."
          },
          {
            "id": "module-1-sub-4-faq-10",
            "question": "Are social media URLs mandatory?",
            "answer": "No. YouTube, Instagram, Facebook, and LinkedIn URLs are optional and can be added or updated in the Profile section later."
          },
          {
            "id": "module-1-sub-4-faq-11",
            "question": "What happens to social URLs entered in Step 3?",
            "answer": "They feed into the Content Manager post-onboarding as digital asset references for content benchmarking and channel strategy."
          }
        ]
      },
      {
        "id": "module-1-sub-5",
        "subModuleTitle": "Step 4 — Terms & Conditions Acceptance",
        "faqs": [
          {
            "id": "module-1-sub-5-faq-1",
            "question": "When is the Terms & Conditions acceptance shown?",
            "answer": "The T&C acceptance card appears when you click the 'Complete Setup' button at the final step of onboarding (Step 4, following Steps 1-3). It is the last action before your onboarding is finalised."
          },
          {
            "id": "module-1-sub-5-faq-2",
            "question": "Do I have to read the full T&C before completing onboarding?",
            "answer": "A concise version of the Terms & Conditions is shown within the acceptance card. You are given the option to view the full Terms & Conditions in your Profile section. You do not need to read the full document to complete onboarding — accepting the summary card is sufficient."
          },
          {
            "id": "module-1-sub-5-faq-3",
            "question": "What happens after I accept the T&C?",
            "answer": "Once you accept, the T&C card closes and your setup is marked as complete. You are then routed directly to the Marketing Dashboard. Your login credentials are delivered via the Welcome Email."
          },
          {
            "id": "module-1-sub-5-faq-4",
            "question": "What if I close the T&C card without accepting?",
            "answer": "You can reopen the card and accept when you are ready. The T&C acceptance is the final step, so onboarding cannot be marked complete until acceptance is confirmed."
          },
          {
            "id": "module-1-sub-5-faq-5",
            "question": "Where can I find the full Terms & Conditions after onboarding?",
            "answer": "The full Terms & Conditions are available in the Help Centre section of your Profile at any time post-onboarding."
          }
        ]
      },
      {
        "id": "module-1-sub-6",
        "subModuleTitle": "Post-Onboarding Routing & Dashboard",
        "faqs": [
          {
            "id": "module-1-sub-6-faq-1",
            "question": "Where am I taken after completing onboarding?",
            "answer": "After completing Step 3 and accepting the T&C, you are routed directly to the Marketing Dashboard using the login credentials delivered via your Welcome Email."
          },
          {
            "id": "module-1-sub-6-faq-2",
            "question": "What is the Marketing Dashboard?",
            "answer": "The Marketing Dashboard is the central hub of Marketing 4SIGHT. It surfaces your platform setup status, module access cards (SEO Manager, Content Manager, GMB Manager, etc.), and contextual prompts to complete any configurations deferred from onboarding."
          },
          {
            "id": "module-1-sub-6-faq-3",
            "question": "What is the Setup Progress Layer on the Dashboard?",
            "answer": "A visual indicator showing completion status across SEO Setup, Social Media manager Setup, and GMB Setup — with direct links to each manager to complete the remaining configuration."
          },
          {
            "id": "module-1-sub-6-faq-4",
            "question": "Will the dashboard prompt me to do things after onboarding?",
            "answer": "Yes. The dashboard shows contextual prompts for pending setups — Data Integration (GSC and GA4) in the SEO Manager, and Channel Access and Content Filter in the Social Media Manager."
          },
          {
            "id": "module-1-sub-6-faq-5",
            "question": "What is the Keyword Planner prompt on the Dashboard?",
            "answer": "Post-onboarding, the dashboard highlights the Keyword Planner setup as a critical next step. This is where your keyword strategy is built using the business classification and portfolio data captured during onboarding."
          },
          {
            "id": "module-1-sub-6-faq-6",
            "question": "For agency-managed accounts, where does onboarding route to?",
            "answer": "Agency users are routed to their agency dashboard post-onboarding if the maximum brand limit (year-to-date) has been reached. If the brand limit has not been reached, they are routed to the Add Brands screen to set up the client brand."
          },
          {
            "id": "module-1-sub-6-faq-7",
            "question": "What is the DIY Guide on the Dashboard?",
            "answer": "The DIY Guide is a help resource accessible from the dashboard sidebar. It provides contextual explanations and info icons throughout the platform. Clicking any info icon opens a tooltip or help content relevant to that specific feature or section."
          },
          {
            "id": "module-1-sub-6-faq-8",
            "question": "What happens to my onboarding data after I reach the Dashboard?",
            "answer": "All onboarding data is permanently stored and reflected across the platform. Profile updates made post-onboarding automatically refresh all connected dashboards. No re-entry of previously submitted information is required at any point."
          }
        ]
      }
    ]
  },
  {
    "id": "module-2",
    "moduleTitle": "Profile",
    "subModules": [
      {
        "id": "module-2-sub-1",
        "subModuleTitle": "Contact & Identity",
        "faqs": [
          {
            "id": "module-2-sub-1-faq-1",
            "question": "What fields are available in the Contact & Identity section of my Profile?",
            "answer": "Contact & Identity contains five fields: Name, Phone Number, Email Address, Designation, and Password. All are viewable. Name, Email, Phone, and Designation are editable (with OTP verification for Email and Phone). Password is changeable via OTP."
          },
          {
            "id": "module-2-sub-1-faq-2",
            "question": "How do I update my name in the Profile?",
            "answer": "Name is a text input field that is viewable and editable. The system validates for alphabetic characters only and trims any leading or trailing whitespace on save. No OTP is required to update your name."
          },
          {
            "id": "module-2-sub-1-faq-3",
            "question": "How do I update my designation?",
            "answer": "Designation is a text input field that is viewable and editable without OTP verification. Update it directly in the Contact & Identity section of your Profile."
          },
          {
            "id": "module-2-sub-1-faq-4",
            "question": "How do I change my phone number?",
            "answer": "Editing your phone number triggers an OTP to the new number. The number must follow E.164 format (country-code aware, e.g., +91 XXXXXXXXXX for India). Verification via OTP is required before the change is saved. If verification fails, the save is blocked."
          },
          {
            "id": "module-2-sub-1-faq-5",
            "question": "How do I change my email address?",
            "answer": "Editing your email address triggers an OTP to the new email for verification before the change is applied. Important security note: changing your email will automatically log out all active sessions across all devices. This is a mandatory security requirement."
          },
          {
            "id": "module-2-sub-1-faq-6",
            "question": "Why does changing my email log me out of all sessions?",
            "answer": "This is a security measure to prevent unauthorised email changes from maintaining unauthorised access. By logging out all sessions upon email change, the platform ensures that only the owner of the new verified email retains access."
          },
          {
            "id": "module-2-sub-1-faq-7",
            "question": "How do I change my password?",
            "answer": "To change your password: (1) Your current password is required as a first verification step. (2) An OTP is sent to your registered email to confirm the change. (3) The new password must meet minimum requirements: at least 8 characters, with at least one uppercase letter, one lowercase letter, one number, and one special character. (4) Once OTP is confirmed, the password is updated."
          },
          {
            "id": "module-2-sub-1-faq-8",
            "question": "Are my phone number and email shown in full on the profile page?",
            "answer": "No. For security, both fields display masked values. For example, an email appears as su****@mail.com and a phone number appears as +91*****1234. The full value is never shown on screen."
          },
          {
            "id": "module-2-sub-1-faq-9",
            "question": "How do I know if my email or phone is verified?",
            "answer": "Verified fields display a green checkmark badge next to them on the Profile page. Unverified fields will not show the badge, and you will be prompted to verify."
          },
          {
            "id": "module-2-sub-1-faq-10",
            "question": "What happens if I enter the OTP incorrectly?",
            "answer": "OTP attempts are rate-limited to a maximum of 3 attempts per 10-minute window. If all 3 attempts fail, the save is blocked and you will need to wait before requesting a new OTP."
          },
          {
            "id": "module-2-sub-1-faq-11",
            "question": "Which email receives my platform notifications, invoices, and reports?",
            "answer": "All notifications, invoices, and reports are delivered to the email address stored in your Contact & Identity section. Ensure this email is correct and verified at all times."
          }
        ]
      },
      {
        "id": "module-2-sub-2",
        "subModuleTitle": "Business Details",
        "faqs": [
          {
            "id": "module-2-sub-2-faq-1",
            "question": "Which business detail fields are available in my Profile?",
            "answer": "The Business Details section contains: Name of Brand (view only), Legal Name of Business (view + edit), Website URL (view + edit), All Service and/or Product Page URLs (view + add + edit), Social Media URLs (view + edit), Logo (view + edit via upload), Colour Codes & Fonts (view + add + edit), Business Address (view + edit via Google Places autocomplete), Business Phone (view + add + edit), and GST (view + edit)."
          },
          {
            "id": "module-2-sub-2-faq-2",
            "question": "Can I change my Brand Name in the Profile?",
            "answer": "No. Brand Name is a view-only field pre-populated from onboarding or admin data. It cannot be edited by the user from the Profile section. Contact Quantyra Analytics support if your Brand Name needs to be corrected."
          },
          {
            "id": "module-2-sub-2-faq-3",
            "question": "Can I change my Legal Business Name?",
            "answer": "Yes. Legal Name of Business is a view and edit field in the Profile. Changes are subject to the validation rules defined in the Profile section."
          },
          {
            "id": "module-2-sub-2-faq-4",
            "question": "How is the Website URL validated in the Profile?",
            "answer": "The system enforces an https:// prefix and validates the basic URL pattern. Validation runs on blur (when you click away from the field) or on save. If the URL is invalid, an inline error is shown and saving is blocked until it is corrected."
          },
          {
            "id": "module-2-sub-2-faq-5",
            "question": "Can I add and manage multiple product and service page URLs?",
            "answer": "Yes. The Service and Product Page URL section supports view, add, and edit operations as a list. You can add multiple entries, remove individual URLs, or edit existing ones. Each URL must pass basic format validation. Each product or service entry should also have a priority tag assigned."
          },
          {
            "id": "module-2-sub-2-faq-6",
            "question": "How do I update my social media profile URLs?",
            "answer": "Social media URLs (Instagram, Facebook, LinkedIn, YouTube, etc.) are viewable and editable in the Business Details section. They follow the same URL validation as website URLs. Updating these links affects content benchmarking in the Content Manager."
          },
          {
            "id": "module-2-sub-2-faq-7",
            "question": "How do I upload or replace my logo?",
            "answer": "Click the logo field in Business Details to upload a new image. Accepted formats are PNG, JPG, and SVG up to a configured file size limit. A cropping and preview tool is displayed before the upload is confirmed. If no logo is set, a default placeholder is shown."
          },
          {
            "id": "module-2-sub-2-faq-8",
            "question": "What colour formats can I enter for brand colours?",
            "answer": "Colour codes must follow hex (e.g., #0859B8), RGB (e.g., rgb(8,89,184)), or HSL format as per the platform's design system. Invalid formats are rejected with an inline validation message."
          },
          {
            "id": "module-2-sub-2-faq-9",
            "question": "Can I select any font for my brand?",
            "answer": "Fonts must be selected from a predefined list or from valid web-safe / system fonts. Arbitrary font names not on the approved list will be rejected with a validation error."
          },
          {
            "id": "module-2-sub-2-faq-10",
            "question": "How does Business Address work in the Profile?",
            "answer": "Business Address uses Google Places API autocomplete. When you start typing, location suggestions appear. Selecting a suggestion auto-populates structured fields: street, city, state, PIN, and country. Manual edits are allowed after autofill, but all mandatory address fields must be completed before saving."
          },
          {
            "id": "module-2-sub-2-faq-11",
            "question": "What happens to the platform when I update my Business Address?",
            "answer": "Updating your business address triggers a GMB recalibration — the system recalculates your local targeting and competitive landscape based on the new location. This ensures your GMB strategy and local SEO recommendations remain accurate."
          },
          {
            "id": "module-2-sub-2-faq-12",
            "question": "What happens to the platform when I update my Website URL?",
            "answer": "Updating the Website URL triggers an SEO re-crawl. The system will re-audit the new URL for keyword extraction, technical SEO health, and page structure. SEO recommendations and audit results will update accordingly."
          },
          {
            "id": "module-2-sub-2-faq-13",
            "question": "What happens to the platform when I update service or product URLs?",
            "answer": "Updating service or product page URLs triggers keyword remapping. The system re-evaluates keyword alignment at the page level and updates SEO page-level optimisation recommendations."
          },
          {
            "id": "module-2-sub-2-faq-14",
            "question": "What happens to the platform when I update my social media URLs?",
            "answer": "Social media URL updates trigger content benchmarking recalibration in theSocial media Manager. The system uses the updated URLs to assess your social presence and competitive content positioning."
          },
          {
            "id": "module-2-sub-2-faq-15",
            "question": "How is Business Phone validated in the Profile?",
            "answer": "Business Phone must follow a valid phone-number format with country code awareness (based on the selected country). If the format is invalid, an inline error is shown and saving is blocked."
          },
          {
            "id": "module-2-sub-2-faq-16",
            "question": "How is GST validated in the Profile?",
            "answer": "GST must follow the valid GSTIN format for the country (15-digit alphanumeric for India). Validation runs inline as you type and again on save. Invalid formats block saving and display an inline error. The system also performs server-side GST validation before applying the save."
          },
          {
            "id": "module-2-sub-2-faq-17",
            "question": "Can updating my profile cause any data to be lost?",
            "answer": "No. The system maintains versioning of all profile data to prevent data loss or incorrect overwrites. A failed save attempt will never auto-clear or overwrite previously saved data. Client-side validation runs first, followed by server-side validation — no partial saves occur."
          }
        ]
      },
      {
        "id": "module-2-sub-3",
        "subModuleTitle": "Subscription & Billing",
        "faqs": [
          {
            "id": "module-2-sub-3-faq-1",
            "question": "Where can I view my current subscription plan?",
            "answer": "Your current plan details — including plan name, included features, billing cycle (monthly or yearly), and account status — are visible in the Subscription & Billing section of your Profile. This section is available to both Free and Enterprise-tier users."
          },
          {
            "id": "module-2-sub-3-faq-2",
            "question": "Can I upgrade my plan from the Profile?",
            "answer": "Yes. The Subscription section includes upgrade and downgrade options. You can initiate a plan change directly from here."
          },
          {
            "id": "module-2-sub-3-faq-3",
            "question": "Can I downgrade my plan?",
            "answer": "Yes. Downgrade options are available in the Subscription section alongside upgrade options. The exact downgrade rules (e.g., data retention, feature access changes) are subject to the final plan terms."
          },
          {
            "id": "module-2-sub-3-faq-4",
            "question": "Where can I view and download past invoices?",
            "answer": "The Payment History section within Subscription & Billing lists all past invoices. Clicking download on any invoice triggers a PDF generation call and initiates a browser download of that invoice immediately."
          },
          {
            "id": "module-2-sub-3-faq-5",
            "question": "Is my subscription history editable?",
            "answer": "No. Subscription history is read-only. It displays a timeline showing: plan start dates, upgrades, renewals, and any plan changes. Historical records cannot be modified by users."
          },
          {
            "id": "module-2-sub-3-faq-6",
            "question": "What does the Subscription History timeline show?",
            "answer": "The timeline shows a chronological history of your account's plan journey: when each plan started, when upgrades or downgrades happened, renewal dates, and any other plan changes. It gives you a complete audit trail of your subscription."
          },
          {
            "id": "module-2-sub-3-faq-7",
            "question": "Is the Subscription & Billing section available on the Free tier?",
            "answer": "Yes. Both Free and Enterprise-tier users have access to the Subscription section. Enterprise users additionally have access to invoice downloads and full payment history."
          }
        ]
      },
      {
        "id": "module-2-sub-4",
        "subModuleTitle": "Help Centre",
        "faqs": [
          {
            "id": "module-2-sub-4-faq-1",
            "question": "What support options are available in the Help Centre?",
            "answer": "Kiki (AI chatbot), Raise a Ticket, Ticket Status tracking, Tutorial Videos, FAQs, Privacy Policy, and Terms & Conditions."
          },
          {
            "id": "module-2-sub-4-faq-2",
            "question": "Who is Kiki?",
            "answer": "Kiki is Marketing 4SIGHT's AI chatbot available across every page of the platform. Ask it anything — it gives instant answers or raises a support ticket for you."
          },
          {
            "id": "module-2-sub-4-faq-3",
            "question": "Can Kiki raise a support ticket?",
            "answer": "Yes. If Kiki can't resolve your issue, it can raise a support ticket for you directly from the chat — no need to go to the Help Centre separately."
          },
          {
            "id": "module-2-sub-4-faq-4",
            "question": "How do I manually raise a support ticket?",
            "answer": "Go to the Help Centre in your Profile section and click Raise Ticket. Fill in your issue details and submit — the team will follow up."
          },
          {
            "id": "module-2-sub-4-faq-5",
            "question": "Where can I check my ticket status?",
            "answer": "In the Help Centre under Ticket Status. Every raised ticket shows its current status so you always know where your issue stands."
          },
          {
            "id": "module-2-sub-4-faq-6",
            "question": "What are Tutorial Videos?",
            "answer": "Short, step-by-step video walkthroughs for every key feature on the platform. Searchable from the Help Centre, mobile-friendly, with Skip and Continue options at your own pace."
          },
          {
            "id": "module-2-sub-4-faq-7",
            "question": "How does the FAQ section work?",
            "answer": "FAQs are searchable with auto-suggestions as you type. Articles are categorised by topic and sorted by most common issues. Each article has a thumbs up / thumbs down so the content improves over time."
          },
          {
            "id": "module-2-sub-4-faq-8",
            "question": "What does the Privacy Policy cover?",
            "answer": "It covers what data we collect, how we use it, who we share it with, how long we keep it, and how we comply with GDPR and CCPA. You are notified whenever it is updated."
          },
          {
            "id": "module-2-sub-4-faq-9",
            "question": "What does the Terms & Conditions cover?",
            "answer": "Your rights as a user, payment terms, what happens if the service is terminated, and liability limits. Acceptance is required at signup and the document is versioned so you can see exactly what changed."
          },
          {
            "id": "module-2-sub-4-faq-10",
            "question": "What happens when Privacy Policy or T&C are updated?",
            "answer": "You receive a notification automatically. Both documents maintain a change log so you can review what was updated and when."
          }
        ]
      },
      {
        "id": "module-2-sub-5",
        "subModuleTitle": "Data Behaviour & Technical Rules",
        "faqs": [
          {
            "id": "module-2-sub-5-faq-1",
            "question": "How quickly do profile changes reflect across the platform?",
            "answer": "Immediately. Every successful profile save emits an update signal. All dashboard components that consume profile data — including the header, sidebar, and company name displays — re-render in real time without requiring a full page reload. There is zero lag between a successful save and dashboard reflection."
          },
          {
            "id": "module-2-sub-5-faq-2",
            "question": "What is the two-layer validation system for profile saves?",
            "answer": "When you save any profile field: (1) Client-side validation runs first. If it fails, the field is highlighted with an inline error message and saving is blocked. (2) If client-side validation passes, server-side validation runs as a second layer. If the server-side check also fails, the user is shown exactly which fields need to be corrected. No partial saves occur — either the full form saves successfully or nothing is saved."
          },
          {
            "id": "module-2-sub-5-faq-3",
            "question": "Will a failed save overwrite my existing correct data?",
            "answer": "No. A failed save attempt will never auto-clear or overwrite previously saved data. Onboarding data in particular — company name, GST, address, email — is permanently stored and protected from being wiped by a failed save."
          },
          {
            "id": "module-2-sub-5-faq-4",
            "question": "What is profile data versioning?",
            "answer": "The system maintains a version history of profile data to prevent data loss and incorrect overwrites. This means every change is tracked, and previous states can be referenced if needed. A future Audit Log enhancement will surface this history as a readable log showing who changed what and when."
          },
          {
            "id": "module-2-sub-5-faq-5",
            "question": "Which platform engines are affected by Profile updates?",
            "answer": "All four core platform engines depend on the Profile section: SEO Manager, GMB Manager, Content Manager, and Funnel Manager. Any profile update automatically triggers recalculations in all dependent modules — including keyword relevance mapping, GMB strategy recalibration, and SEO recommendations. Strategy modules (SIGO framework) are also re-evaluated on profile changes."
          },
          {
            "id": "module-2-sub-5-faq-6",
            "question": "What triggers an SEO re-crawl from the Profile?",
            "answer": "Updating the Website URL in Business Details triggers an SEO re-crawl. The system will re-audit the new domain for keyword extraction and technical health."
          },
          {
            "id": "module-2-sub-5-faq-7",
            "question": "What triggers GMB recalibration?",
            "answer": "Updating the Business Address or location in the Profile triggers a GMB recalibration. The system updates local targeting, local competitor mapping, and GMB strategy recommendations."
          },
          {
            "id": "module-2-sub-5-faq-8",
            "question": "What triggers keyword remapping?",
            "answer": "Updating Service or Product Page URLs in the Profile triggers keyword remapping. Page-level keyword alignment and SEO recommendations are recalculated for the updated pages."
          },
          {
            "id": "module-2-sub-5-faq-9",
            "question": "What is the success notification after saving Profile changes?",
            "answer": "After a successful profile save, a toast notification is shown on screen: 'Profile updated successfully.' All dashboard components refresh immediately following this notification."
          },
          {
            "id": "module-2-sub-5-faq-10",
            "question": "Is there a planned Audit Log for profile changes?",
            "answer": "Yes. A future enhancement will introduce a read-only Audit Log within the Profile section. It will display a timestamped record of all profile changes — showing which field was changed, what it was changed to, and when the change occurred."
          }
        ]
      }
    ]
  },
  {
    "id": "module-3",
    "moduleTitle": "Website Builder",
    "subModules": [
      {
        "id": "module-3-sub-1",
        "subModuleTitle": "Access & Overview",
        "faqs": [
          {
            "id": "module-3-sub-1-faq-1",
            "question": "What is the Website Builder?",
            "answer": "A guided, AI-powered tool that generates a complete, SEO-ready website for your business in minutes — no coding or agency needed."
          },
          {
            "id": "module-3-sub-1-faq-2",
            "question": "Who is it built for?",
            "answer": "Startups and small businesses that don't yet have a live website and need a marketing-ready digital presence quickly."
          },
          {
            "id": "module-3-sub-1-faq-3",
            "question": "Is this a drag-and-drop website editor?",
            "answer": "No. It generates structured content packages (files you download and publish). There is no live visual editor."
          },
          {
            "id": "module-3-sub-1-faq-4",
            "question": "Does Marketing 4SIGHT host my website?",
            "answer": "No. All outputs are downloadable files. You publish the website through your own hosting provider. The platform may suggest partners like Hostinger or GoDaddy."
          },
          {
            "id": "module-3-sub-1-faq-5",
            "question": "What does the Website Builder focus on?",
            "answer": "Speed, structure, and marketing readiness — not advanced design customisation or enterprise web development."
          },
          {
            "id": "module-3-sub-1-faq-6",
            "question": "How do I access the Website Builder?",
            "answer": "Two ways: click the greyed-out SEO or GMB tile on your dashboard and select 'Build My Website', or access it directly from the main dashboard navigation at any time."
          },
          {
            "id": "module-3-sub-1-faq-7",
            "question": "Why are SEO Manager and GMB Manager greyed out?",
            "answer": "Both require a valid website URL to function. If you skipped the URL during onboarding, they stay locked until you add one."
          },
          {
            "id": "module-3-sub-1-faq-8",
            "question": "How do SEO and GMB Manager get unlocked?",
            "answer": "Once you complete the Website Builder and save your Website URL in your Profile, both modules unlock automatically — no manual action needed."
          },
          {
            "id": "module-3-sub-1-faq-9",
            "question": "I already have a website. Can I still use the Website Builder?",
            "answer": "Yes. The Website Builder is accessible to all users from the dashboard navigation, regardless of whether a URL is already saved."
          },
          {
            "id": "module-3-sub-1-faq-10",
            "question": "What happens when I click the greyed-out SEO or GMB tile?",
            "answer": "A tooltip appears: 'It looks like you don't have a website yet.' Clicking 'Build My Website' takes you directly into the Website Builder."
          }
        ]
      },
      {
        "id": "module-3-sub-2",
        "subModuleTitle": "Sitemap & Page Structure",
        "faqs": [
          {
            "id": "module-3-sub-2-faq-1",
            "question": "How is my sitemap generated?",
            "answer": "It is auto-generated based on your Business Type (Service / Product / Both) entered during onboarding. You can enable or disable optional pages before final generation."
          },
          {
            "id": "module-3-sub-2-faq-2",
            "question": "Which pages are included by default?",
            "answer": "Home, About Us, Blog, Gallery, and Contact Us are included for all business types. Services Landing Page is added for Service or Both types. Product Landing Page is added for Product or Both types."
          },
          {
            "id": "module-3-sub-2-faq-3",
            "question": "Can I remove Home, About, or Contact pages?",
            "answer": "No. These are mandatory structural pages required for minimum website completeness and SEO readiness."
          },
          {
            "id": "module-3-sub-2-faq-4",
            "question": "What are optional pages?",
            "answer": "Individual Service Sub-pages, Individual Product Sub-pages, and Custom Pages are optional — you can enable or disable them before generation."
          }
        ]
      },
      {
        "id": "module-3-sub-3",
        "subModuleTitle": "Page Content (Home, About, Services, Blog, Gallery, Contact)",
        "faqs": [
          {
            "id": "module-3-sub-3-faq-1",
            "question": "My business has both services and products. Do I get separate pages?",
            "answer": "Yes. Separate landing pages are generated for each to avoid mixed information architecture and SEO dilution."
          },
          {
            "id": "module-3-sub-3-faq-2",
            "question": "Can I add custom pages?",
            "answer": "Yes. Custom pages can be added as optional additions beyond the standard sitemap structure."
          },
          {
            "id": "module-3-sub-3-faq-3",
            "question": "What sections does the Home page have?",
            "answer": "Sliding Hero Banner (3–5 slides with headline, sub-headline, CTA), Brand USP Strip, Brand Story Snippet, Services/Products card grid, Gallery Strip, Latest Blog previews, Contact Form, Social Media Links, and a Top Banner with brand name and tagline."
          },
          {
            "id": "module-3-sub-3-faq-4",
            "question": "Where do hero banner images come from?",
            "answer": "From your Gallery uploads. If none are available, stock placeholder images are used."
          },
          {
            "id": "module-3-sub-3-faq-5",
            "question": "Where does the USP Strip content come from?",
            "answer": "Auto-derived from the Brand Differentiators you entered in onboarding Step 3."
          },
          {
            "id": "module-3-sub-3-faq-6",
            "question": "What does the About Us page include?",
            "answer": "Brand Story (auto-drafted from your Business Description), an optional Founder Story, and an optional Team Section where you add member name, role, and photo."
          },
          {
            "id": "module-3-sub-3-faq-7",
            "question": "Is the Founder Story or Team Section mandatory?",
            "answer": "No. Both are optional. The sections are simply omitted if left empty."
          },
          {
            "id": "module-3-sub-3-faq-8",
            "question": "What does the Services or Products page include?",
            "answer": "Problem-Solution Block, Service/Product Cards (priority items first), USP Callouts, Client Testimonials placeholder, Our Clients logo strip, and 3–5 auto-generated FAQs editable by you."
          },
          {
            "id": "module-3-sub-3-faq-9",
            "question": "Where does the Problem-Solution Block content come from?",
            "answer": "Generated from your service/product inputs and Business Category using AI."
          },
          {
            "id": "module-3-sub-3-faq-10",
            "question": "Can I add client testimonials and logos?",
            "answer": "Yes. Both are optional — enter quotes and client names manually, or upload/link client logos."
          },
          {
            "id": "module-3-sub-3-faq-11",
            "question": "What does the Blog page include?",
            "answer": "A card grid listing (thumbnail, title, date, excerpt), a structured blog post template (H1, author, date, category, body, CTA), an Add New Blog form, and auto-generation via Content Manager."
          },
          {
            "id": "module-3-sub-3-faq-12",
            "question": "What URL structure do blog posts follow?",
            "answer": "SEO-friendly hierarchy: /blog/post-name. Auto-generated and editable."
          },
          {
            "id": "module-3-sub-3-faq-13",
            "question": "Can the Content Manager auto-generate blog posts?",
            "answer": "Yes. Blog posts can be generated via Content Manager and sync automatically to the Blog page."
          },
          {
            "id": "module-3-sub-3-faq-14",
            "question": "What SEO fields exist per blog post?",
            "answer": "Meta Title, Meta Description, and URL Slug — all pre-filled by Content Manager and editable."
          },
          {
            "id": "module-3-sub-3-faq-15",
            "question": "What media types does the Gallery support?",
            "answer": "Image uploads and video embeds via YouTube or Vimeo URL."
          },
          {
            "id": "module-3-sub-3-faq-16",
            "question": "What tag categories are available?",
            "answer": "Awards, Behind the Scenes (BTS), Media, Events, Products, and Services."
          },
          {
            "id": "module-3-sub-3-faq-17",
            "question": "Is tagging assets mandatory?",
            "answer": "Yes. Every uploaded asset must be tagged — required for the category filter to work on the public gallery page."
          },
          {
            "id": "module-3-sub-3-faq-18",
            "question": "What does the Contact Us page include?",
            "answer": "Contact Form (Name, Email, optional Phone, Message), Subscribe Now Form (Name, Email), Address display, Google Map Embed, and optional Business Hours."
          },
          {
            "id": "module-3-sub-3-faq-19",
            "question": "Where do Contact Form submissions go?",
            "answer": "To your dashboard email from onboarding, and visible in your platform dashboard."
          },
          {
            "id": "module-3-sub-3-faq-20",
            "question": "What does the Subscribe Now form do?",
            "answer": "Collects visitor Name and Email into a contact list visible and manageable in your dashboard."
          },
          {
            "id": "module-3-sub-3-faq-21",
            "question": "Is the Google Map Embed automatic?",
            "answer": "Yes. Auto-generated from your onboarding address. You can confirm or adjust the pin location manually."
          },
          {
            "id": "module-3-sub-3-faq-22",
            "question": "What does the footer contain on every page?",
            "answer": "Brand Logo, Navigation Links, Social Media Icons, Address and Phone, Privacy Policy link (placeholder — you must update), and a Copyright line auto-populated with your business name and year."
          },
          {
            "id": "module-3-sub-3-faq-23",
            "question": "Is the website Privacy Policy the same as the platform's?",
            "answer": "No. It is a separate auto-generated placeholder. You must review and replace it with your actual privacy terms before publishing."
          }
        ]
      },
      {
        "id": "module-3-sub-4",
        "subModuleTitle": "Design & Branding (Templates, Colours, Fonts)",
        "faqs": [
          {
            "id": "module-3-sub-4-faq-1",
            "question": "How are my brand colours determined?",
            "answer": "Automatically extracted from your logo uploaded in onboarding Step 3 — dominant colour becomes Primary, secondary becomes Accent. You can override any colour manually."
          },
          {
            "id": "module-3-sub-4-faq-2",
            "question": "What if I didn't upload a logo during onboarding?",
            "answer": "System defaults are used. Upload your logo in the Profile section or inside the Website Builder to trigger automatic colour extraction."
          },
          {
            "id": "module-3-sub-4-faq-3",
            "question": "How are fonts chosen?",
            "answer": "Font preferences are collected in onboarding Step 3. Inside the builder, you select one heading font and one body font from a curated Google Fonts pairing list."
          },
          {
            "id": "module-3-sub-4-faq-4",
            "question": "What templates are available?",
            "answer": "Six templates by business vertical: General/Startup (clean, minimal), Restaurant/Cafe (warm tones), Salon/Beauty (soft palette, appointment-focused), Clinic/Healthcare (trust-focused, accessible), Retail/E-commerce Lite (product-card heavy), and Professional Services (corporate, credential-focused)."
          },
          {
            "id": "module-3-sub-4-faq-5",
            "question": "Does choosing a different template change my content?",
            "answer": "No. Template selection only changes the visual layer — colours, fonts, spacing, component style. Content and page structure remain the same."
          },
          {
            "id": "module-3-sub-4-faq-6",
            "question": "Will my website be mobile-friendly?",
            "answer": "Yes. All templates automatically adapt to mobile, tablet, and desktop. Navigation collapses to a hamburger menu on mobile screens."
          },
          {
            "id": "module-3-sub-4-faq-7",
            "question": "How do I select a design theme in the Website Builder?",
            "answer": "You have two selection options: (1) Brand Colour / Logo Based — the system extracts colours from your uploaded logo and applies them to your chosen template automatically. (2) Template Based — you browse and pick from the pre-designed template library by visual style, independent of your brand colours."
          },
          {
            "id": "module-3-sub-4-faq-8",
            "question": "What if I update my logo or colours in the Profile later?",
            "answer": "Re-open the Website Builder, re-generate the template, and the design theme updates automatically to reflect your latest brand assets."
          },
          {
            "id": "module-3-sub-4-faq-9",
            "question": "Is template selection mandatory?",
            "answer": "Yes. Template and font pair selection are both required steps in the design stage."
          }
        ]
      },
      {
        "id": "module-3-sub-5",
        "subModuleTitle": "SEO Foundation",
        "faqs": [
          {
            "id": "module-3-sub-5-faq-1",
            "question": "What SEO elements are generated for each page?",
            "answer": "H1 Tag (one per page), H2/H3 Tags, Meta Title (max 60 chars), Meta Description (max 150 chars), URL Slug (lowercase, hyphenated), Alt Text for images, Robots.txt template, and Sitemap.xml draft."
          },
          {
            "id": "module-3-sub-5-faq-2",
            "question": "Can I edit the auto-generated SEO fields?",
            "answer": "Yes. All SEO fields — H1, Meta Title, Meta Description, URL Slug, and Alt Text — are editable before you download the final output."
          },
          {
            "id": "module-3-sub-5-faq-3",
            "question": "What is the Meta Title character limit?",
            "answer": "60 characters maximum, aligned with Google's recommended display length to avoid truncation in search results."
          },
          {
            "id": "module-3-sub-5-faq-4",
            "question": "What is the Meta Description character limit?",
            "answer": "150 characters maximum, within standard SERP display limits."
          },
          {
            "id": "module-3-sub-5-faq-5",
            "question": "What is a URL Slug?",
            "answer": "The page-specific part of a URL (e.g., /our-services). Auto-generated as lowercase and hyphenated. Fully editable."
          },
          {
            "id": "module-3-sub-5-faq-6",
            "question": "What is the Robots.txt file and what do I do with it?",
            "answer": "A plain-text file instructing search crawlers which pages to access. Download it and upload it to your hosting provider's root directory."
          },
          {
            "id": "module-3-sub-5-faq-7",
            "question": "What is the Sitemap.xml and what do I do with it?",
            "answer": "An XML file listing all your pages to help search engines index them. Download it, upload it to your host, then submit it to Google Search Console once your site is live."
          },
          {
            "id": "module-3-sub-5-faq-8",
            "question": "When does the SEO Foundation activate?",
            "answer": "Once your website is live on your hosting domain. GA4 and GSC tracking only activate after the site is published and the tracking codes are added to your host."
          }
        ]
      },
      {
        "id": "module-3-sub-6",
        "subModuleTitle": "Analytics Setup (GA4 & GSC)",
        "faqs": [
          {
            "id": "module-3-sub-6-faq-1",
            "question": "What analytics setup does the Website Builder provide?",
            "answer": "A pre-configured GA4 tracking code snippet and a GSC verification file/HTML tag — both with step-by-step guides to add them to your hosting platform."
          },
          {
            "id": "module-3-sub-6-faq-2",
            "question": "Does Marketing 4SIGHT add the tracking codes automatically?",
            "answer": "No. Since the platform doesn't host your website, you add the codes yourself. The platform provides the exact codes and guides."
          },
          {
            "id": "module-3-sub-6-faq-3",
            "question": "When does GA4 tracking start working?",
            "answer": "Once your website is live on your domain and the GA4 snippet is correctly added to your hosting platform."
          },
          {
            "id": "module-3-sub-6-faq-4",
            "question": "What happens after GA4/GSC are set up and my site goes live?",
            "answer": "Update your Website URL in the Profile and the SEO Manager and GMB Manager unlock automatically. The standard SEO and keyword flow then begins."
          }
        ]
      },
      {
        "id": "module-3-sub-7",
        "subModuleTitle": "Outputs & Downloads",
        "faqs": [
          {
            "id": "module-3-sub-7-faq-1",
            "question": "What inputs does the Website Builder collect beyond onboarding?",
            "answer": "Founder Name & Story (About Us), Team Members (About Us), Client Testimonials & Logos (Services/Products), FAQ edits (Services/Products), Gallery Assets with tags (Gallery), Business Hours (Contact Us), Google Map pin confirmation (Contact Us), Template selection, Font pair selection, Colour overrides, and Blog posts."
          },
          {
            "id": "module-3-sub-7-faq-2",
            "question": "Which inputs are mandatory?",
            "answer": "Only Template Selection and Font Pair Selection. Gallery Tag is required only if you upload a gallery asset. Everything else is optional."
          },
          {
            "id": "module-3-sub-7-faq-3",
            "question": "Can I fill in optional sections later?",
            "answer": "Yes. Optional inputs can be added or updated and the output re-generated at any time."
          },
          {
            "id": "module-3-sub-7-faq-4",
            "question": "What do I receive after completing the Website Builder?",
            "answer": "Eight downloads: Website Sitemap (PDF + editable outline), Page-wise Content Pack (PDF/DOCX per page), Design Template Preview (PDF mockup), SEO Foundation File (PDF with meta tags per page), Robots.txt Template (.txt), Sitemap.xml Draft (XML), GA4 Tracking Setup Guide (PDF), and GSC Verification Guide (PDF)."
          },
          {
            "id": "module-3-sub-7-faq-5",
            "question": "What is the Page-wise Content Pack?",
            "answer": "Draft content for every page — headlines, body copy, CTAs — generated via Content Manager. One file per page in PDF or DOCX format."
          },
          {
            "id": "module-3-sub-7-faq-6",
            "question": "What is the Design Template Preview?",
            "answer": "A PDF mockup showing how your chosen template looks with your brand colours, fonts, and page layout before you hand it to a developer or hosting platform."
          },
          {
            "id": "module-3-sub-7-faq-7",
            "question": "Do I need a developer to use these outputs?",
            "answer": "Not necessarily. The step-by-step guides minimise technical complexity, but tasks like uploading codes to your host require basic familiarity with your hosting provider."
          },
          {
            "id": "module-3-sub-7-faq-8",
            "question": "What appears on my dashboard after completing the builder?",
            "answer": "A post-builder checklist: Download Sitemap, Download Content Pack, Download SEO Files, and Setup GA/GSC."
          }
        ]
      },
      {
        "id": "module-3-sub-8",
        "subModuleTitle": "Platform Integrations & Limitations",
        "faqs": [
          {
            "id": "module-3-sub-8-faq-1",
            "question": "How does the Website Builder connect to the rest of the platform?",
            "answer": "Blog posts sync to Content Manager, social URL updates reflect in footer/header output, and saving your Website URL in Profile automatically unlocks SEO and GMB Manager."
          },
          {
            "id": "module-3-sub-8-faq-2",
            "question": "If I update social URLs, does the website output update?",
            "answer": "Yes. Social URL changes in your Profile automatically reflect in the footer and header of your generated website output."
          },
          {
            "id": "module-3-sub-8-faq-3",
            "question": "If I add blog posts in the Website Builder, do they appear in Content Manager?",
            "answer": "Yes. Blog content is synced — you can manage posts from either interface."
          },
          {
            "id": "module-3-sub-8-faq-4",
            "question": "Does it host or publish my website?",
            "answer": "No. All outputs are downloadable files. Hosting is your responsibility."
          },
          {
            "id": "module-3-sub-8-faq-5",
            "question": "Does it manage domain registration or DNS?",
            "answer": "No. Domain and DNS setup must be handled directly with your registrar and hosting provider."
          },
          {
            "id": "module-3-sub-8-faq-6",
            "question": "Can I sell products through the generated website?",
            "answer": "No. E-commerce transactions and product catalogue management are out of scope."
          },
          {
            "id": "module-3-sub-8-faq-7",
            "question": "Does it handle ongoing SEO management?",
            "answer": "No. Ongoing SEO (keywords, rankings, backlinks) is managed in the SEO Manager, which unlocks once your site is live."
          },
          {
            "id": "module-3-sub-8-faq-8",
            "question": "Does it connect my social media accounts for publishing?",
            "answer": "No. Social media OAuth connections are set up in the Content Manager. The builder only embeds your social URLs in the website footer/header."
          },
          {
            "id": "module-3-sub-8-faq-9",
            "question": "Is there a live drag-and-drop editor?",
            "answer": "No. The builder produces structured content packages (downloadable files), not a live editing environment."
          }
        ]
      }
    ]
  },
  {
    "id": "module-4",
    "moduleTitle": "GMB Manager",
    "subModules": [
      {
        "id": "module-4-sub-1",
        "subModuleTitle": "Overview & Purpose",
        "faqs": [
          {
            "id": "module-4-sub-1-faq-1",
            "question": "What is the GMB Manager?",
            "answer": "A performance system within Marketing 4SIGHT that helps local businesses manage and improve their Google Business Profile (GBP) visibility, engagement, and conversions — built on the SIGO framework (Strategy, Implementation, Governance, Optimization)."
          },
          {
            "id": "module-4-sub-1-faq-2",
            "question": "What is Google Business Profile (GBP)?",
            "answer": "Formerly Google My Business (GMB), it is the Google platform that lets businesses appear on Google Search and Maps with their address, hours, reviews, photos, and posts."
          },
          {
            "id": "module-4-sub-1-faq-3",
            "question": "Who is the GMB Manager built for?",
            "answer": "Businesses with a physical or local presence — gyms, restaurants, salons, clinics, schools, garages, coaching centres, and any brand that depends on local customers finding them online."
          },
          {
            "id": "module-4-sub-1-faq-4",
            "question": "What are the core objectives of GMB management?",
            "answer": "Appear in local search and Maps (Local Pack), drive calls/directions/clicks, build trust via reviews, improve local SEO through keyword-rich content, and convert profile visits into leads."
          },
          {
            "id": "module-4-sub-1-faq-5",
            "question": "What KPIs does GMB Manager track?",
            "answer": "Keyword rank (local), website clicks, calls, direction requests, messages/WhatsApp (if enabled), total reviews, and average rating — each showing current value, previous period, and % change."
          },
          {
            "id": "module-4-sub-1-faq-6",
            "question": "Does GMB Manager work independently of the rest of the platform?",
            "answer": "No. It works in direct dependency with onboarding inputs (business type, location, presence) and the Keyword Planner's local keyword universe."
          }
        ]
      },
      {
        "id": "module-4-sub-2",
        "subModuleTitle": "Business Classification & GMB Visibility",
        "faqs": [
          {
            "id": "module-4-sub-2-faq-1",
            "question": "What is a SEO First brand?",
            "answer": "A business with no location dependency (SaaS, ecommerce, digital services). Keywords are 100% non-local. GMB is not prioritised."
          },
          {
            "id": "module-4-sub-2-faq-2",
            "question": "What is a Hybrid brand?",
            "answer": "A business with both physical stores and online presence. Keywords are split 40–60% between local and non-local, with both GMB and SEO active."
          },
          {
            "id": "module-4-sub-2-faq-3",
            "question": "How does the platform classify my brand type?",
            "answer": "Classification happens during onboarding based on your Business Type, Physical Presence Indicator, and Location Scope. The platform uses this to determine keyword split and which managers are active."
          },
          {
            "id": "module-4-sub-2-faq-4",
            "question": "What if my business is purely online — does GMB Manager still appear?",
            "answer": "No. If no local keywords are generated or your business is purely online, the GMB Manager card is hidden or greyed out with the message: 'GMB not applicable based on your business model.'"
          },
          {
            "id": "module-4-sub-2-faq-5",
            "question": "Can a menswear brand with both a store and a website use GMB?",
            "answer": "Yes — that is a Hybrid brand. 'Menswear store near me' routes to GMB; 'buy mens shirts online India' routes to SEO. Both managers are active."
          },
          {
            "id": "module-4-sub-2-faq-6",
            "question": "What happens the first time I click GMB Manager?",
            "answer": "The platform triggers a GMB Setup Layer to collect additional inputs and authenticate your Google Business Profile. Once done, the system runs an audit and activates all four SIGO layers."
          },
          {
            "id": "module-4-sub-2-faq-7",
            "question": "What mandatory inputs are required to activate GMB Manager?",
            "answer": "Three blocking inputs: GMB Profile URL, Google Account Authentication (OAuth), and Business Name (auto-fetched with editable validation)."
          }
        ]
      },
      {
        "id": "module-4-sub-3",
        "subModuleTitle": "First-Time Setup & Authentication",
        "faqs": [
          {
            "id": "module-4-sub-3-faq-1",
            "question": "What are conditional inputs?",
            "answer": "Inputs collected only if a gap is detected: business category confirmation (if mismatch found), service areas (if not defined in GMB), WhatsApp/messaging enablement status, and store type (single location — mandatory in V1)."
          },
          {
            "id": "module-4-sub-3-faq-2",
            "question": "What optional inputs can I provide?",
            "answer": "Target service radius (e.g., 3km / 5km / 10km) and priority services/products for content and keyword alignment. These are recommended but not blocking."
          },
          {
            "id": "module-4-sub-3-faq-3",
            "question": "What does the system do after authentication?",
            "answer": "It runs four processes: GMB API data sync, profile audit engine, local SERP competitor scraping, and KPI baseline extraction. This generates your full SIGO output."
          },
          {
            "id": "module-4-sub-3-faq-4",
            "question": "What if authentication fails?",
            "answer": "You are shown a fallback with two options: Book a free 15-minute call with the team (Virtual Agency support), or follow a self-serve guide to create/claim/verify your listing and reconnect."
          },
          {
            "id": "module-4-sub-3-faq-5",
            "question": "Can I skip GMB setup and come back later?",
            "answer": "Yes. A 'Set up later' option is available. The GMB section on your dashboard will show as locked with a banner prompting you to complete the connection."
          },
          {
            "id": "module-4-sub-3-faq-6",
            "question": "Where does competitor data come from?",
            "answer": "Two sources: (1) Competitors you entered during onboarding — used for reference and benchmarking. (2) System-generated local SERP competitors — scraped from top 3–10 results for '[service] near me' and '[service] in [location]'."
          },
          {
            "id": "module-4-sub-3-faq-7",
            "question": "What data is extracted from local SERP competitors?",
            "answer": "Business Name, Rating, Review count, Category, and Posting activity."
          }
        ]
      },
      {
        "id": "module-4-sub-4",
        "subModuleTitle": "Competition Logic",
        "faqs": [
          {
            "id": "module-4-sub-4-faq-1",
            "question": "What is Profile Health for competitors?",
            "answer": "An estimated score combining visible signals: photo count, review freshness, rating, category accuracy, and posting frequency."
          },
          {
            "id": "module-4-sub-4-faq-2",
            "question": "Are aggregators like JustDial and Sulekha included as competitors?",
            "answer": "No. Aggregators (JustDial, Sulekha, IndiaMART, etc.) are excluded from competitor results."
          },
          {
            "id": "module-4-sub-4-faq-3",
            "question": "What happens if no competitors are found?",
            "answer": "The system first expands the search radius. If still none are found, it shows 'Low competition zone.'"
          },
          {
            "id": "module-4-sub-4-faq-4",
            "question": "How many competitors are shown?",
            "answer": "Top 5–10 local competitors, each showing rating, review count, activity level, profile completeness, and profile health estimate."
          }
        ]
      },
      {
        "id": "module-4-sub-5",
        "subModuleTitle": "Strategy (SIGO Layer 1) — Profile Health, Goals & Action Plan",
        "faqs": [
          {
            "id": "module-4-sub-5-faq-1",
            "question": "What is the Profile Health Score?",
            "answer": "A 0–100 score measuring how complete and optimised your Google Business Profile is. It is broken down by category so you know exactly where points are being lost."
          },
          {
            "id": "module-4-sub-5-faq-2",
            "question": "What categories make up the Profile Health Score?",
            "answer": "Business Information (NAP completeness, website, phone), Business Description (750-char, keyword-optimised), Categories (primary + secondary), Hours (weekly + holiday hours), Photos (cover, profile, interior, exterior, product, team), Posts (at least one in last 7 days), Reviews (4+ star avg, responses within 48hrs), Features (messaging, booking links), and Products/Services (at least 3 listed with descriptions)."
          },
          {
            "id": "module-4-sub-5-faq-3",
            "question": "What goals does the GMB Manager track?",
            "answer": "Eight goals: Keyword Rank (top 3 for 5–10 priority local keywords), Website Clicks (10–15% MoM growth), Calls (min 20/month), Direction Requests (15–20/month), WhatsApp messages (10–15/month if enabled), GMB Chat Messages (15–20/month if enabled), Total Reviews (5–10 new/month), and Average Rating (maintain ≥4.5 stars)."
          },
          {
            "id": "module-4-sub-5-faq-4",
            "question": "Can review targets be guaranteed by the action plan?",
            "answer": "No. Reviews depend on customer action. A disclaimer is shown: 'There may not be a direct correlation between the action plan and review count.'"
          },
          {
            "id": "module-4-sub-5-faq-5",
            "question": "How is the Action Plan structured?",
            "answer": "Split into two categories: Technical Action Plan (one-time setup tasks + regular maintenance activities) and Content Plan (4-week GMB posting calendar)."
          },
          {
            "id": "module-4-sub-5-faq-6",
            "question": "What are One-Time Setup Activities?",
            "answer": "Profile verification (green badge), setting exact NAP, adding a 750-character keyword-optimised description, selecting primary category and up to 10 attributes, uploading 20+ geotagged photos, and creating a detailed product/service list with prices."
          },
          {
            "id": "module-4-sub-5-faq-7",
            "question": "What are Regular Maintenance Activities?",
            "answer": "Weekly: update hours, publish 1 post, request 5+ reviews, respond to all reviews within 24hrs. Monthly: review categories based on performance queries, audit insights. Quarterly: add 3–5 new photos/videos, update services and prices."
          },
          {
            "id": "module-4-sub-5-faq-8",
            "question": "What is the 'Near Me' Keyword Opportunities section?",
            "answer": "A list of high-value local search phrases (e.g., '[service] near me', '[service] in [city]', 'best [service] nearby') the business should appear for — suggested for use in descriptions, posts, and listings."
          },
          {
            "id": "module-4-sub-5-faq-9",
            "question": "What does the Description & Keyword Improvement Guide do?",
            "answer": "It reviews your current description, highlights gaps (too short, missing service terms, NAP mismatches), suggests location-specific phrases, and lets you edit and save an improved version directly."
          },
          {
            "id": "module-4-sub-5-faq-10",
            "question": "What does the Review Management Plan include?",
            "answer": "Current rating, total reviews, response rate, pre-built response templates for positive and negative reviews, a 24–48 hour response time target, and an alert if rating drops below 4 stars."
          }
        ]
      },
      {
        "id": "module-4-sub-6",
        "subModuleTitle": "Implementation (SIGO Layer 2) — Photo Upload, Post Scheduler, Calendar, Q&A & Reviews",
        "faqs": [
          {
            "id": "module-4-sub-6-faq-1",
            "question": "What does the Implementation module contain?",
            "answer": "Four sub-tabs: Implementation Overview (task tracking), Photo Upload, Post Creation & Scheduling, and Q&A & Checklist + Review Management."
          },
          {
            "id": "module-4-sub-6-faq-2",
            "question": "What does the Implementation Overview show?",
            "answer": "Four tabs: One-Time Tasks (from Strategy Action Plan), Weekly Tasks (from Regular Maintenance), Photos Uploaded (from Photo Upload tab), and Posts Scheduled (from Content Plan)."
          },
          {
            "id": "module-4-sub-6-faq-3",
            "question": "What photo types can I upload?",
            "answer": "Profile photo (250×250px), Cover photo (1080×608px), Google Posts image (1200×900px), and Business/interior/exterior/product photos (720×720px). All accept JPG or PNG, 10KB–5MB."
          },
          {
            "id": "module-4-sub-6-faq-4",
            "question": "How many photos can I upload?",
            "answer": "Up to 20 photos/videos in V1. Uploaded assets are saved in a gallery and can be used for GMB posts."
          },
          {
            "id": "module-4-sub-6-faq-5",
            "question": "Can I preview how photos look on the GMB listing before publishing?",
            "answer": "Yes. After selecting the photo type from a dropdown, the platform shows a contextual preview (e.g., how the cover photo will appear on the listing) before it is published."
          },
          {
            "id": "module-4-sub-6-faq-6",
            "question": "What can I do in the Post Scheduler?",
            "answer": "Write post text, select post type (Offer, What's New, or Event), attach an image or video, and queue posts weeks in advance."
          },
          {
            "id": "module-4-sub-6-faq-7",
            "question": "What are the three GMB post types supported?",
            "answer": "Offers (discounts, deals, promotions with start/end dates), What's New (store updates, new services, announcements), and Events (sales events, community activities with title, dates, and time)."
          },
          {
            "id": "module-4-sub-6-faq-8",
            "question": "What can I edit in each post?",
            "answer": "Image/video (from gallery or upload), CTA button, caption, and title. Note: changing a keyword in the caption triggers an alert and the change cannot be saved. Post dates cannot be changed."
          },
          {
            "id": "module-4-sub-6-faq-9",
            "question": "What tabs appear in the Post Scheduler?",
            "answer": "Published (posts marked as published), Drafts (total posts minus published), and Total Posts (all edited and saved posts). Posts can also be downloaded to upload manually to GMB."
          },
          {
            "id": "module-4-sub-6-faq-10",
            "question": "What is the 4-Week GMB Content Calendar?",
            "answer": "An auto-generated posting schedule of 8 posts across 4 weeks (~2 per week), covering: Business Introduction, Educational content, Offers/Promotions, Testimonials, Product/Service Highlights, Behind-the-Scenes, and Events/Seasonal posts."
          },
          {
            "id": "module-4-sub-6-faq-11",
            "question": "Is the calendar the same for every business?",
            "answer": "The structure (8 posts, 4 weeks) is standardised. The content is dynamically generated per business using its type, local keywords, services, goals, and location. A gym gets fitness tips and membership offers; a restaurant gets food promotions."
          },
          {
            "id": "module-4-sub-6-faq-12",
            "question": "What does each generated post include?",
            "answer": "Post title, caption, suggested CTA (Call Now, Visit Website, Get Directions, Book Now), recommended post type (Offer/What's New/Event), and suggested image direction."
          },
          {
            "id": "module-4-sub-6-faq-13",
            "question": "Can I edit the generated posts?",
            "answer": "Yes. All 8 posts are saved as editable drafts. You can edit content, change the image, change the CTA, and approve or reschedule posts. Edits sync back to the Action Plan screen automatically."
          },
          {
            "id": "module-4-sub-6-faq-14",
            "question": "What does the Q&A Manager do?",
            "answer": "Shows all customer questions on your listing. You can write and publish answers manually, and flag inappropriate questions for removal."
          },
          {
            "id": "module-4-sub-6-faq-15",
            "question": "What does Review Management include?",
            "answer": "A Review Overview (total reviews, avg rating, response rate, rating trend), a Review Feed (latest 50–100 reviews prioritised by urgency — unresponded, recent, negative), and Review Response Assistance."
          },
          {
            "id": "module-4-sub-6-faq-16",
            "question": "How does Review Response Assistance work?",
            "answer": "Select any unresponded review and the platform generates a suggested response based on sentiment, content, business type, and tone. You can edit, copy, or mark it as posted. Response style auto-adjusts for positive, neutral, and negative reviews."
          },
          {
            "id": "module-4-sub-6-faq-17",
            "question": "What is the Progress Checklist?",
            "answer": "A step-by-step tracker showing completed and outstanding profile health items, each linked directly to the relevant editor."
          }
        ]
      },
      {
        "id": "module-4-sub-7",
        "subModuleTitle": "Governance (SIGO Layer 3)",
        "faqs": [
          {
            "id": "module-4-sub-7-faq-1",
            "question": "What does the Governance module do?",
            "answer": "Acts as the operational monitoring and control layer — tracking profile changes, monitoring alerts, managing access, and ensuring profile health and activity consistency."
          },
          {
            "id": "module-4-sub-7-faq-2",
            "question": "What is the Live Profile Preview?",
            "answer": "Shows exactly how your listing appears to someone searching on Google right now, updated in real time as changes are made and published."
          },
          {
            "id": "module-4-sub-7-faq-3",
            "question": "What alerts does the system generate?",
            "answer": "Alerts for: new reviews received, reviews pending response beyond 48hrs, rating dropping below threshold, Google-suggested edits detected, no posting activity for 7 days, missing/outdated business info, profile completeness score drops, and authentication/access issues."
          },
          {
            "id": "module-4-sub-7-faq-4",
            "question": "How are alerts prioritised?",
            "answer": "Three severity levels: Critical, Warning, and Informational."
          },
          {
            "id": "module-4-sub-7-faq-5",
            "question": "What does the Activity Log track?",
            "answer": "All platform actions: post edits, profile updates, photo uploads, generated review responses, publishing actions, and user activity timestamps — each showing the action, user name, date/time, and status."
          },
          {
            "id": "module-4-sub-7-faq-6",
            "question": "What does Live Profile Review monitor?",
            "answer": "Business hours status, missing profile information, profile completeness changes, category changes, NAP consistency, photo availability, and posting activity consistency."
          },
          {
            "id": "module-4-sub-7-faq-7",
            "question": "What does Publishing & Activity Monitoring track?",
            "answer": "Posts published, posting frequency consistency, upcoming scheduled posts, and inactive periods. Alerts or recommendations are generated if posting drops or the calendar goes empty."
          }
        ]
      },
      {
        "id": "module-4-sub-8",
        "subModuleTitle": "Optimization (SIGO Layer 4)",
        "faqs": [
          {
            "id": "module-4-sub-8-faq-1",
            "question": "What does Performance Trends track?",
            "answer": "Profile impressions, website clicks, calls, direction requests, messages/WhatsApp, profile views, review growth, and average rating trend — viewable weekly or monthly with current value, previous period, and % change."
          },
          {
            "id": "module-4-sub-8-faq-2",
            "question": "What does Search Query Insights show?",
            "answer": "The actual local search queries driving discovery on Google Search and Maps: top-performing keywords, new queries detected, high-impression keywords, low-conversion keywords, keyword trend movement, and gap/expansion opportunities."
          },
          {
            "id": "module-4-sub-8-faq-3",
            "question": "Where do Search Query Insights feed back into?",
            "answer": "Content planning, keyword optimisation, and business description improvements."
          },
          {
            "id": "module-4-sub-8-faq-4",
            "question": "What does Review & Reputation Insights analyse?",
            "answer": "Rating trend, positive vs negative review ratio, review growth, response pending trend, and common review themes (service quality, staff, pricing, waiting time, product quality)."
          },
          {
            "id": "module-4-sub-8-faq-5",
            "question": "What does Content Performance track?",
            "answer": "Posting frequency, post consistency, content type distribution, engagement trends, periods of inactivity, content gaps, underutilised post types, and best-performing content themes."
          },
          {
            "id": "module-4-sub-8-faq-6",
            "question": "What does Engagement Insights analyse?",
            "answer": "Peak days and hours for calls/directions/messages, seasonal spikes, and customer action preferences — e.g., 'Most direction requests occur on weekends' or 'Calls spike after promotional posts.'"
          }
        ]
      },
      {
        "id": "module-4-sub-9",
        "subModuleTitle": "Keyword Handling",
        "faqs": [
          {
            "id": "module-4-sub-9-faq-1",
            "question": "What kind of action recommendations does the system generate?",
            "answer": "Data-driven prompts such as: 'No posts published in 14 days', 'Direction requests declined this month', 'Competitors have significantly higher photo activity', 'High-impression keywords missing from description', and 'Negative reviews increased this month.'"
          },
          {
            "id": "module-4-sub-9-faq-2",
            "question": "What is the Monthly Audit & Improvement Tracking?",
            "answer": "The platform automatically re-runs profile analysis periodically and compares it to previous audits — highlighting improvements, declines, pending opportunities, KPI movement, and profile health score changes."
          },
          {
            "id": "module-4-sub-9-faq-3",
            "question": "Where do GMB keywords come from?",
            "answer": "From the Utility manager Keyword Planner (a standalone Marketing Dashboard utility). GMB Manager only receives the local keyword subset — broader keywords go to SEO Manager."
          },
          {
            "id": "module-4-sub-9-faq-4",
            "question": "What if no local keywords were suggested by the platform?",
            "answer": "The system uses default local keywords for which the brand is already getting visibility. For new brands with no default local keywords, the platform suggests potential local keywords and corresponding activities."
          },
          {
            "id": "module-4-sub-9-faq-5",
            "question": "What if a user edits a keyword in a post caption?",
            "answer": "The system triggers an alert and blocks the save. Keywords in post captions are protected to maintain local SEO alignment."
          }
        ]
      },
      {
        "id": "module-4-sub-10",
        "subModuleTitle": "Edge Cases",
        "faqs": [
          {
            "id": "module-4-sub-10-faq-1",
            "question": "What happens if I don't have a Google Business Profile?",
            "answer": "The platform detects this and shows a friendly fallback with two options: (A) Book a free 15-minute call with the team (Virtual Agency support), or (B) Follow a self-serve guide to create, claim, and verify your listing — then reconnect."
          },
          {
            "id": "module-4-sub-10-faq-2",
            "question": "Why does GMB profile verification matter?",
            "answer": "Until your listing is verified, Google won't show it fully in Search or Maps. Verification confirms to Google that your business is real and at that address."
          },
          {
            "id": "module-4-sub-10-faq-3",
            "question": "What if I have a GMB profile but it's not verified (no green badge)?",
            "answer": "The platform detects verification = false and prompts you to complete verification to unlock full functionality, or book a call with the team for guided support."
          },
          {
            "id": "module-4-sub-10-faq-4",
            "question": "What happens if my business appears multiple times on Google (duplicate listings)?",
            "answer": "The system detects duplicates (same name + same address) and flags them in Governance Alerts with the message: 'Multiple listings detected. This may split ranking signals. Consider merging or removing duplicates.' The platform does not merge automatically."
          }
        ]
      }
    ]
  },
  {
    "id": "module-5",
    "moduleTitle": "Social Media Manager",
    "subModules": [
      {
        "id": "module-5-sub-1",
        "subModuleTitle": "General",
        "faqs": [
          {
            "id": "module-5-sub-1-faq-1",
            "question": "What is Social Media Manager?",
            "answer": "module inside Marketing 4Sight to plan, create, manage, and optimize social media content across Instagram, Facebook, LinkedIn, and YouTube."
          },
          {
            "id": "module-5-sub-1-faq-2",
            "question": "Does it auto-publish content?",
            "answer": "No. The platform prepares and organizes content; users publish manually on their social platforms."
          },
          {
            "id": "module-5-sub-1-faq-3",
            "question": "What platforms are supported?",
            "answer": "Instagram, Facebook, LinkedIn, and YouTube."
          },
          {
            "id": "module-5-sub-1-faq-4",
            "question": "What is the SIGO framework?",
            "answer": "Strategy, Implementation, Governance, and Optimization — the four layers that structure the entire Social Media Manager workflow."
          },
          {
            "id": "module-5-sub-1-faq-5",
            "question": "Who is this module for?",
            "answer": "Business owners, marketing teams, and agency members managing social media operations."
          }
        ]
      },
      {
        "id": "module-5-sub-2",
        "subModuleTitle": "First-Time Setup",
        "faqs": [
          {
            "id": "module-5-sub-2-faq-1",
            "question": "What happens when I open Social Media Manager for the first time?",
            "answer": "one-time setup flow is triggered to connect platforms, configure your brand voice, and initialize AI content systems."
          },
          {
            "id": "module-5-sub-2-faq-2",
            "question": "Do I need to re-enter my business details?",
            "answer": "No. Business, audience, and brand information from onboarding are already available."
          },
          {
            "id": "module-5-sub-2-faq-3",
            "question": "What permissions does the platform need?",
            "answer": "Engagement analytics, reach data, audience insights, comment visibility, post performance, watch time, and subscriber metrics."
          },
          {
            "id": "module-5-sub-2-faq-4",
            "question": "What if I already added social URLs during onboarding?",
            "answer": "The platform auto-detects them and requests permissions directly — no need to re-enter."
          },
          {
            "id": "module-5-sub-2-faq-5",
            "question": "Will I see this setup flow again?",
            "answer": "No. After first setup, you go directly to the SIGO dashboard on every visit."
          }
        ]
      },
      {
        "id": "module-5-sub-3",
        "subModuleTitle": "Brand Communication Setup",
        "faqs": [
          {
            "id": "module-5-sub-3-faq-1",
            "question": "What is Communication Tone?",
            "answer": "Your brand's voice style — e.g. Professional, Friendly, Educational, Bold. Used by AI to generate captions and scripts."
          },
          {
            "id": "module-5-sub-3-faq-2",
            "question": "What is Brand Personality?",
            "answer": "How your brand is positioned — e.g. Thought Leader, Innovative, Approachable. Shapes narrative and content framing."
          },
          {
            "id": "module-5-sub-3-faq-3",
            "question": "What is Platform Priority Ranking?",
            "answer": "drag-and-drop ordering of your platforms (e.g. Instagram first, LinkedIn second) that influences content distribution."
          },
          {
            "id": "module-5-sub-3-faq-4",
            "question": "Can I add competitor social profiles?",
            "answer": "Yes. Competitor names and social URLs can be added for benchmarking and trend analysis."
          }
        ]
      },
      {
        "id": "module-5-sub-4",
        "subModuleTitle": "Content Planning Model",
        "faqs": [
          {
            "id": "module-5-sub-4-faq-1",
            "question": "What is Regular Content?",
            "answer": "Standalone posts around themes, topics, education, engagement, or awareness — not tied to a specific campaign."
          },
          {
            "id": "module-5-sub-4-faq-2",
            "question": "What is Campaign-Based Content?",
            "answer": "sequence of interconnected posts built around one larger idea, launch, event, or narrative arc."
          },
          {
            "id": "module-5-sub-4-faq-3",
            "question": "Can I use both Regular and Campaign content?",
            "answer": "Yes. The platform creates a hybrid calendar that balances both types."
          },
          {
            "id": "module-5-sub-4-faq-4",
            "question": "How many campaign posts can I have per plan?",
            "answer": "Bronze: max 25%, Silver: max 35%, Gold: max 45% of your monthly total."
          },
          {
            "id": "module-5-sub-4-faq-5",
            "question": "What are Social Observance posts?",
            "answer": "Posts aligned to cultural, social, or industry dates like International Women's Day, Diwali, Earth Day, etc."
          },
          {
            "id": "module-5-sub-4-faq-6",
            "question": "How many observance posts can I select?",
            "answer": "Maximum 4 per calendar cycle — applicable on all plans."
          },
          {
            "id": "module-5-sub-4-faq-7",
            "question": "Do observance posts count toward my monthly limit?",
            "answer": "Yes. They are deducted from the total monthly content allocation."
          }
        ]
      },
      {
        "id": "module-5-sub-5",
        "subModuleTitle": "Strategy",
        "faqs": [
          {
            "id": "module-5-sub-5-faq-1",
            "question": "What does the Strategy module do?",
            "answer": "It analyzes your brand, platforms, audience, and competitors — then generates a recommended monthly content calendar."
          },
          {
            "id": "module-5-sub-5-faq-2",
            "question": "Is the Strategy calendar final?",
            "answer": "No. It is a recommended starting point. You can edit everything in Implementation."
          },
          {
            "id": "module-5-sub-5-faq-3",
            "question": "What is the Platform Activity Score?",
            "answer": "High/Medium/Low score showing how active and consistent your posting has been on each platform."
          },
          {
            "id": "module-5-sub-5-faq-4",
            "question": "What is the Content Health Index?",
            "answer": "combined score evaluating content consistency, format diversity, and engagement stability across platforms."
          },
          {
            "id": "module-5-sub-5-faq-5",
            "question": "What is Platform Imbalance Detection?",
            "answer": "If one platform is very active and another is inactive, the system flags a 'Channel imbalance detected' alert."
          },
          {
            "id": "module-5-sub-5-faq-6",
            "question": "What is a Communication Signature?",
            "answer": "combined output of your tone and personality — e.g. 'Insight-led professional storytelling with authority-driven educational tone.'"
          },
          {
            "id": "module-5-sub-5-faq-7",
            "question": "How are competitors analyzed?",
            "answer": "By evaluating their posting frequency, content formats, engagement rates, follower counts, and dominant themes."
          },
          {
            "id": "module-5-sub-5-faq-8",
            "question": "What is the monthly post limit per plan?",
            "answer": "Bronze: 14 posts, Silver: 20 posts, Gold: 30 posts (indicative)."
          },
          {
            "id": "module-5-sub-5-faq-9",
            "question": "What content themes does the platform generate?",
            "answer": "Product, Service, Offer, Promotional, Event, Award, Educational, Awareness, Engagement, Testimonial, Authority, Campaign, and Observance posts."
          },
          {
            "id": "module-5-sub-5-faq-10",
            "question": "Can one theme dominate the calendar?",
            "answer": "No. No single theme can exceed 35% of total monthly content."
          }
        ]
      },
      {
        "id": "module-5-sub-6",
        "subModuleTitle": "Implementation",
        "faqs": [
          {
            "id": "module-5-sub-6-faq-1",
            "question": "What can I do in Implementation?",
            "answer": "Edit captions, change formats, upload media, rewrite scripts with AI, reorder posts, and download final content packages."
          },
          {
            "id": "module-5-sub-6-faq-2",
            "question": "Can I change the date of a post?",
            "answer": "Yes, via drag-and-drop. Observance posts cannot be moved outside their assigned dates."
          },
          {
            "id": "module-5-sub-6-faq-3",
            "question": "Can I edit captions?",
            "answer": "Yes — you can manually edit or regenerate using an AI prompt."
          },
          {
            "id": "module-5-sub-6-faq-4",
            "question": "Can I edit images?",
            "answer": "Yes, using AI image prompts. Video editing is not supported — you can only replace/upload videos."
          },
          {
            "id": "module-5-sub-6-faq-5",
            "question": "What formats does the platform support?",
            "answer": "Static images, infographics, carousels (multi-image), video files (MP4, MOV), and AI-generated visual edits."
          },
          {
            "id": "module-5-sub-6-faq-6",
            "question": "How do I publish content?",
            "answer": "Download the content package and upload manually to the respective social platform."
          },
          {
            "id": "module-5-sub-6-faq-7",
            "question": "What does a download package include?",
            "answer": "Caption, hashtags, platform name, format type, and attached media files."
          },
          {
            "id": "module-5-sub-6-faq-8",
            "question": "Can I download all posts at once?",
            "answer": "Yes — you can download a single post, a full campaign bundle, or the entire monthly calendar."
          },
          {
            "id": "module-5-sub-6-faq-9",
            "question": "What happens when I edit a post?",
            "answer": "The Strategy calendar updates in real time and AI recalculates distribution and campaign balance automatically."
          }
        ]
      },
      {
        "id": "module-5-sub-7",
        "subModuleTitle": "Governance",
        "faqs": [
          {
            "id": "module-5-sub-7-faq-1",
            "question": "What does Governance track?",
            "answer": "The execution status of every content piece — whether it's done, posted, pending, or overdue."
          },
          {
            "id": "module-5-sub-7-faq-2",
            "question": "What statuses can a post have?",
            "answer": "Done and Posted."
          },
          {
            "id": "module-5-sub-7-faq-3",
            "question": "What alerts does Governance generate?",
            "answer": "Overdue posts, pending execution, incomplete campaigns, observance misses, and execution backlog."
          },
          {
            "id": "module-5-sub-7-faq-4",
            "question": "Does Governance track performance?",
            "answer": "Yes — it tracks likes, comments, shares, saves, impressions, reach, watch time, and click-throughs."
          },
          {
            "id": "module-5-sub-7-faq-5",
            "question": "What is the Content Performance Score (CPS)?",
            "answer": "weighted score for each post based on engagement rate, sentiment, reach efficiency, and platform benchmarks."
          },
          {
            "id": "module-5-sub-7-faq-6",
            "question": "What is Audience Sentiment Intelligence?",
            "answer": "Comments are clustered into positive feedback, complaints, queries, buying intent, and passive engagement."
          },
          {
            "id": "module-5-sub-7-faq-7",
            "question": "Does Governance feed data back into Strategy?",
            "answer": "Yes. Performance signals are continuously sent to Optimization and then back to Strategy for recalibration."
          }
        ]
      },
      {
        "id": "module-5-sub-8",
        "subModuleTitle": "Optimization",
        "faqs": [
          {
            "id": "module-5-sub-8-faq-1",
            "question": "What does Optimization do?",
            "answer": "It converts performance data, market trends, and audience behavior into predictive recommendations and strategy improvements."
          },
          {
            "id": "module-5-sub-8-faq-2",
            "question": "What is the Content Efficiency Score (CES)?",
            "answer": "score evaluating each post's engagement quality, sentiment, share/save ratio, and market trend alignment."
          },
          {
            "id": "module-5-sub-8-faq-3",
            "question": "What is Content Fatigue Detection?",
            "answer": "The system detects if a content type or theme is overused and causing declining audience response."
          },
          {
            "id": "module-5-sub-8-faq-4",
            "question": "What external sources does Optimization use?",
            "answer": "Industry reports (HubSpot, LinkedIn B2B Institute, Sprout Social), Google Trends, Meta Trend Reports, BuzzSumo, and platform trend feeds."
          },
          {
            "id": "module-5-sub-8-faq-5",
            "question": "What is Platform Evolution Intelligence?",
            "answer": "It tracks new platform features, algorithm changes, policy updates, and generates adoption recommendations."
          },
          {
            "id": "module-5-sub-8-faq-6",
            "question": "What are examples of Optimization signals?",
            "answer": "'Educational carousels outperforming promotional posts', 'Instagram prioritizing saves over likes', 'LinkedIn native documents receiving high reach.'"
          },
          {
            "id": "module-5-sub-8-faq-7",
            "question": "Does the platform learn over time?",
            "answer": "Yes. The Continuous Learning Engine improves recommendation models, scoring logic, and theme weighting with every content cycle."
          },
          {
            "id": "module-5-sub-8-faq-8",
            "question": "What is Predictive Strategy Recalibration?",
            "answer": "The system forecasts rising content formats and declining ones, then adjusts your future content plan accordingly."
          }
        ]
      },
      {
        "id": "module-5-sub-9",
        "subModuleTitle": "Plans & Limits",
        "faqs": [
          {
            "id": "module-5-sub-9-faq-1",
            "question": "How many posts do I get per month?",
            "answer": "Bronze: 14, Silver: 20, Gold: 30 (indicative numbers)."
          },
          {
            "id": "module-5-sub-9-faq-2",
            "question": "Can I exceed my monthly post limit?",
            "answer": "No. Implementation cannot exceed the Strategy-defined monthly allocation for your plan."
          },
          {
            "id": "module-5-sub-9-faq-3",
            "question": "Are observance posts included in the monthly limit?",
            "answer": "Yes, they count toward your total monthly allocation."
          },
          {
            "id": "module-5-sub-9-faq-4",
            "question": "Is the campaign cap different per plan?",
            "answer": "Yes — Bronze 25%, Silver 35%, Gold 45% of total posts can be campaign content."
          }
        ]
      }
    ]
  },
  {
    "id": "module-6",
    "moduleTitle": "SEO Manager",
    "subModules": [
      {
        "id": "module-6-sub-1",
        "subModuleTitle": "General",
        "faqs": [
          {
            "id": "module-6-sub-1-faq-1",
            "question": "What is SEO Manager?",
            "answer": "An AI-assisted module inside Marketing 4Sight that guides users through strategy, implementation, governance, and optimization of organic search engine performance."
          },
          {
            "id": "module-6-sub-1-faq-2",
            "question": "Who is SEO Manager for?",
            "answer": "Business owners, marketing teams, and agency members managing website SEO."
          },
          {
            "id": "module-6-sub-1-faq-3",
            "question": "Does it work for new websites too?",
            "answer": "Yes — it handles both new websites (foundational setup) and existing websites (audit, diagnosis, and optimization)."
          },
          {
            "id": "module-6-sub-1-faq-4",
            "question": "What is the SIGO framework?",
            "answer": "Strategy, Implementation, Governance, and Optimization — the four layers that structure the entire SEO Manager workflow."
          },
          {
            "id": "module-6-sub-1-faq-5",
            "question": "What data powers SEO Manager?",
            "answer": "Brand identity, business type, geographic scope, competitor domains, website URL from onboarding, plus Google Search Console (GSC) and Google Analytics (GA4) integration."
          }
        ]
      },
      {
        "id": "module-6-sub-2",
        "subModuleTitle": "Activation & Access",
        "faqs": [
          {
            "id": "module-6-sub-2-faq-1",
            "question": "When does SEO Manager become available?",
            "answer": "Only after completing the Keyword Universe Finalization in the Utility Manager."
          },
          {
            "id": "module-6-sub-2-faq-2",
            "question": "What else is needed to activate SEO Manager?",
            "answer": "website URL entered during onboarding. Without it,  SEO Manager stays disabled."
          },
          {
            "id": "module-6-sub-2-faq-3",
            "question": "What if I don't have a GA/GSC account?",
            "answer": "You can still use SEO Manager. The keyword universe will be view-only — you can choose and lock keywords from the Utility Manager without GSC integration."
          },
          {
            "id": "module-6-sub-2-faq-4",
            "question": "What does the disabled state look like?",
            "answer": "greyed-out module with the message: 'Complete Website Setup and Finalize Your SEO Keyword Universe to Activate SEO Manager.'"
          },
          {
            "id": "module-6-sub-2-faq-5",
            "question": "Can I edit keywords inside SEO Manager?",
            "answer": "No. Keywords are managed exclusively in the Utility Manager. SEO Manager is read-only for keywords."
          }
        ]
      },
      {
        "id": "module-6-sub-3",
        "subModuleTitle": "Strategy",
        "faqs": [
          {
            "id": "module-6-sub-3-faq-1",
            "question": "What does the Strategy section show?",
            "answer": "top-level dashboard with key metrics, a 3-month strategy snapshot, goals, and action plan overview."
          },
          {
            "id": "module-6-sub-3-faq-2",
            "question": "What is the As-Is State?",
            "answer": "diagnostic baseline of your current SEO performance — keyword rankings, SERP positioning, organic traffic, and competitive position — generated from GSC and GA data."
          },
          {
            "id": "module-6-sub-3-faq-3",
            "question": "What are the four As-Is baseline cards?",
            "answer": "Keyword Rankings, SERP Positioning, Organic Traffic, and Competitive Position — each clickable for a detailed breakdown."
          },
          {
            "id": "module-6-sub-3-faq-4",
            "question": "What SEO parameters are checked in As-Is?",
            "answer": "On-Page SEO (page targeting, meta, content structure, accessibility, URLs), Off-Page SEO (backlinks, authority, anchor text, brand mentions), and Core Web Vitals & Technical SEO (crawl, canonicalization, page experience, AI crawl readiness)."
          },
          {
            "id": "module-6-sub-3-faq-5",
            "question": "What parameter statuses are shown?",
            "answer": "Optimal or Needs Attention — with sub-parameters and detailed scoring visible via an info icon."
          },
          {
            "id": "module-6-sub-3-faq-6",
            "question": "What are the default SEO goals?",
            "answer": "Top 3: Increase Organic Traffic (+500/month), Improve Keyword Rankings (top 50/20/10 distribution), Capture SERP Features. Additional: Reduce Average Position, Increase Impressions (+2,000/month), Improve Domain Authority (+4 points)."
          },
          {
            "id": "module-6-sub-3-faq-7",
            "question": "Are goals customizable?",
            "answer": "Goals are system-generated for every user and locked for a 3-month benchmarking cycle. They are refreshed based on performance trends after the cycle ends."
          },
          {
            "id": "module-6-sub-3-faq-8",
            "question": "What is the Action Plan?",
            "answer": "3-month task roadmap generated from As-Is gaps, keyword universe, and active goals — covering On-Page, Off-Page, Technical SEO, and Content tasks."
          },
          {
            "id": "module-6-sub-3-faq-9",
            "question": "How are Action Plan tasks prioritized?",
            "answer": "By impact-to-effort ratio relative to goal alignment."
          },
          {
            "id": "module-6-sub-3-faq-10",
            "question": "How long are goals and action plans locked?",
            "answer": "3 months. After evaluation, new goals and tasks are recommended based on performance trends, competitive benchmarks, and AI modeling."
          }
        ]
      },
      {
        "id": "module-6-sub-4",
        "subModuleTitle": "Implementation",
        "faqs": [
          {
            "id": "module-6-sub-4-faq-1",
            "question": "What does Implementation do?",
            "answer": "Transforms the SEO strategy into executable assets — content, meta tags, images, blog posts, FAQs, and off-page submissions."
          },
          {
            "id": "module-6-sub-4-faq-2",
            "question": "What does the Implementation Overview show?",
            "answer": "Monthly content production, on-page and off-page production counts, completion percentage, and pending/published asset counts."
          },
          {
            "id": "module-6-sub-4-faq-3",
            "question": "What On-Page assets can be generated?",
            "answer": "Homepage/service/product/landing page content, meta titles, meta descriptions, H1 and H2 tags, image ALT text, internal linking suggestions, FAQ content, and blog articles."
          },
          {
            "id": "module-6-sub-4-faq-4",
            "question": "What Off-Page assets can be generated?",
            "answer": "Web 2.0 articles, classified ads, social bookmarking content, PDF submission assets, business listing content, directory submission content, image submission content, Q&A answers, guest post drafts, and press releases."
          },
          {
            "id": "module-6-sub-4-faq-5",
            "question": "Which platforms are supported for Web 2.0 articles?",
            "answer": "Medium, Blogger, and WordPress.com."
          },
          {
            "id": "module-6-sub-4-faq-6",
            "question": "Which platforms are targeted for Q&A submissions?",
            "answer": "Quora, Reddit, and forums."
          },
          {
            "id": "module-6-sub-4-faq-7",
            "question": "What word count rules apply to off-page content?",
            "answer": "Articles: 400–800 words. Classified ads: 150–300 words. Q&A answers: 150–400 words with one link maximum and a natural brand mention (not as the opening line)."
          },
          {
            "id": "module-6-sub-4-faq-8",
            "question": "How does Image Optimizer work?",
            "answer": "Two modes: Website Scanner (detects images over 500 KB and compresses to WebP, max 150 KB standard / 300 KB hero) and Standalone Uploader (accepts any image and returns compressed download with ALT text and filename suggestions)."
          },
          {
            "id": "module-6-sub-4-faq-9",
            "question": "What meta tags does the Meta Generator cover?",
            "answer": "Title, meta description, meta robots, canonical, viewport, og:title, og:description, og:image, twitter:card, hreflang, and ALT text — with a live SERP preview and exportable HTML snippet."
          },
          {
            "id": "module-6-sub-4-faq-10",
            "question": "How many blog posts can be generated per month?",
            "answer": "Up to 4 blog posts per month."
          },
          {
            "id": "module-6-sub-4-faq-11",
            "question": "How often are FAQ outputs cached?",
            "answer": "Generated FAQs for a given URL are cached for 30 days. FAQ generation covers a maximum of 2 pages (Home + one Service/Product page)."
          },
          {
            "id": "module-6-sub-4-faq-12",
            "question": "What intelligence is used to generate all assets?",
            "answer": "Keyword Universe + Brand Context + Business Category + Competitor Intelligence + SEO Best Practices."
          }
        ]
      },
      {
        "id": "module-6-sub-5",
        "subModuleTitle": "Governance",
        "faqs": [
          {
            "id": "module-6-sub-5-faq-1",
            "question": "What does Governance do?",
            "answer": "Tracks real-time progress across As-Is performance, goal achievement, and action plan completion — sourced from GSC and GA."
          },
          {
            "id": "module-6-sub-5-faq-2",
            "question": "How often is Governance data refreshed?",
            "answer": "Daily where possible, monthly at minimum."
          },
          {
            "id": "module-6-sub-5-faq-3",
            "question": "What are the three Governance benchmarks?",
            "answer": "As-Is Performance Score (baseline), Goals Progress (goals on track ÷ total), and Action Plan Completion (tasks done ÷ total)."
          },
          {
            "id": "module-6-sub-5-faq-4",
            "question": "What are the Performance Health Scores?",
            "answer": "On-Page Score, Off-Page Score, and Core Web Vitals Score — each a weighted aggregate of underlying parameters shown as progress bars."
          },
          {
            "id": "module-6-sub-5-faq-5",
            "question": "How is parameter scoring calculated?",
            "answer": "Optimal = 100, Needs Attention = 50, Critical = 0. Group score = average of sub-parameters. 80–100 = Optimal, 50–79 = Improved, below 50 = Needs Attention."
          },
          {
            "id": "module-6-sub-5-faq-6",
            "question": "What is shown on each Goal card in Governance?",
            "answer": "Status, days left, progress percentage, baseline, current and target values, trend direction, and monthly milestone checkpoints."
          },
          {
            "id": "module-6-sub-5-faq-7",
            "question": "How long does the goal tracking cycle last?",
            "answer": "90 days. After that, new goals are recommended based on performance history, competitive benchmarks, and AI modeling."
          },
          {
            "id": "module-6-sub-5-faq-8",
            "question": "How is Action Plan tracking organized?",
            "answer": "By category — On-Page, Off-Page, Core Web Vitals — with completion rates, overdue task highlights, and recent task activity."
          },
          {
            "id": "module-6-sub-5-faq-9",
            "question": "Does completing a task automatically update the As-Is State?",
            "answer": "Not yet directly — users click 'Mark as Complete' manually. Auto-scraping to verify completion on the website is planned for future versions."
          }
        ]
      },
      {
        "id": "module-6-sub-6",
        "subModuleTitle": "Optimization",
        "faqs": [
          {
            "id": "module-6-sub-6-faq-1",
            "question": "What does the Optimization section provide?",
            "answer": "Monthly strategic SEO insights covering ecosystem changes, algorithm updates, competitor shifts, and growth opportunities."
          },
          {
            "id": "module-6-sub-6-faq-2",
            "question": "How often does Optimization refresh?",
            "answer": "Monthly."
          },
          {
            "id": "module-6-sub-6-faq-3",
            "question": "What is the Ecosystem Monitor?",
            "answer": "competitive intelligence dashboard covering performance decline alerts, emerging competitors, growth opportunities, and algorithm update impacts."
          },
          {
            "id": "module-6-sub-6-faq-4",
            "question": "How are emerging competitors ranked?",
            "answer": "By threat level — calculated as overlap density × ranking velocity."
          },
          {
            "id": "module-6-sub-6-faq-5",
            "question": "What are Growth Opportunities?",
            "answer": "High-potential keywords with favorable volume-to-difficulty ratios and strategic scores derived from GSC and GA engagement data."
          },
          {
            "id": "module-6-sub-6-faq-6",
            "question": "What is Foresight Recommends?",
            "answer": "premium AI-generated strategy section covering Expert SEO Audit, Advanced Link Building, Content Strategy Frameworks, and Technical SEO Mastery. It is locked and requires a subscription upgrade."
          },
          {
            "id": "module-6-sub-6-faq-7",
            "question": "What does Foresight Recommends include when unlocked?",
            "answer": "Proprietary frameworks, implementation guidance from GSC/GA/NLP analysis, and curated expert insights — updated monthly based on performance shifts and algorithm changes."
          }
        ]
      }
    ]
  },
  {
    "id": "module-7",
    "moduleTitle": "Utility Manager (Keyword Planner)",
    "subModules": [
      {
        "id": "module-7-sub-1",
        "subModuleTitle": "What Is Keyword Planning?",
        "faqs": [
          {
            "id": "module-7-sub-1-faq-1",
            "question": "What is a keyword?",
            "answer": "A keyword is the exact word or phrase someone types into Google when searching for a product or service — for example, 'salon near me' or 'buy men's kurta online'."
          },
          {
            "id": "module-7-sub-1-faq-2",
            "question": "Why do keywords matter for my business?",
            "answer": "When your business shows up for the right keywords, the right customers find you. Keywords are the bridge between what people search and what you offer."
          },
          {
            "id": "module-7-sub-1-faq-3",
            "question": "Do I have to research keywords myself?",
            "answer": "No. The platform does it all for you. Just fill in your business details during onboarding and the system builds your keyword list automatically."
          },
          {
            "id": "module-7-sub-1-faq-4",
            "question": "How does the platform know which keywords to pick for me?",
            "answer": "It uses the information you provided during onboarding — your business name, description, products, services, location, and competitors — to build a keyword list tailored specifically to your business."
          },
          {
            "id": "module-7-sub-1-faq-5",
            "question": "What if I don't have a website yet — can I still get keywords?",
            "answer": "Yes. The platform generates keywords from your business description and services even if you have no website. You don't need a website to start."
          },
          {
            "id": "module-7-sub-1-faq-6",
            "question": "How long does keyword generation take?",
            "answer": "It runs automatically in the background after onboarding. You don't need to wait or do anything extra."
          },
          {
            "id": "module-7-sub-1-faq-7",
            "question": "Will I see different keywords if my business is local vs online?",
            "answer": "Yes. A local business (like a salon or clinic) gets location-based keywords like 'salon in Kolkata'. An online business gets broader keywords like 'buy kurta online India'. A business with both gets both types."
          },
          {
            "id": "module-7-sub-1-faq-8",
            "question": "What is a local keyword?",
            "answer": "A local keyword includes a location — like 'gym near me' or 'best bakery in Mumbai'. These are used to attract customers in your area and are routed to your Google Business Profile (GMB)."
          },
          {
            "id": "module-7-sub-1-faq-9",
            "question": "What is an organic keyword?",
            "answer": "An organic keyword is a search term people use to find businesses online without a location — like 'how to style a blazer' or 'best accounting software'. These are used for your website's SEO."
          },
          {
            "id": "module-7-sub-1-faq-10",
            "question": "What is a keyword cluster?",
            "answer": "A group of related keywords that all mean roughly the same thing. Instead of creating a separate page for each keyword, one page can rank for the entire group — saving you effort and improving results."
          },
          {
            "id": "module-7-sub-1-faq-11",
            "question": "Do I need to understand all of this to use the platform?",
            "answer": "No. The platform handles the technical side completely. You just review the suggested keywords, mark the ones you want, and the platform does the rest."
          },
          {
            "id": "module-7-sub-1-faq-12",
            "question": "Can I add or remove keywords after the list is generated?",
            "answer": "Yes. You can review your keyword list, filter by relevance or volume, and select the ones that feel right for your business. The platform guides you through the selection."
          },
          {
            "id": "module-7-sub-1-faq-13",
            "question": "What does 'keyword difficulty' mean?",
            "answer": "It shows how hard it is to rank on Google for a particular keyword. High difficulty means many strong competitors are already ranking for it. The platform helps you pick a healthy mix of easy and competitive keywords."
          },
          {
            "id": "module-7-sub-1-faq-14",
            "question": "What does 'search volume' mean?",
            "answer": "It is the number of times people search for a keyword every month. Higher volume means more people are looking — but it also tends to mean more competition."
          },
          {
            "id": "module-7-sub-1-faq-15",
            "question": "What is keyword intent?",
            "answer": "It describes why someone is searching. 'How to lose weight' is informational — they want to learn. 'Gym membership near me' is transactional — they are ready to act. The platform categorises your keywords by intent so the right content reaches people at the right moment."
          },
          {
            "id": "module-7-sub-1-faq-16",
            "question": "What happens to my keywords after I select them?",
            "answer": "They are automatically sent to the right places — local keywords go to your GMB Manager, organic keywords go to your SEO Manager, and content keywords go to your Content Manager. Everything is connected."
          },
          {
            "id": "module-7-sub-1-faq-17",
            "question": "Do my keywords ever update?",
            "answer": "Yes. As your business grows and market trends change, the platform surfaces new keyword opportunities and flags underperforming ones in the Optimization layer."
          }
        ]
      },
      {
        "id": "module-7-sub-2",
        "subModuleTitle": "Where Do Keywords Come From?",
        "faqs": [
          {
            "id": "module-7-sub-2-faq-1",
            "question": "How does the platform know what keywords to give me?",
            "answer": "It reads your website, your business description, the products and services you listed, your location, and your competitors' websites — then builds a keyword list that matches your business."
          },
          {
            "id": "module-7-sub-2-faq-2",
            "question": "What if I haven't written much on my website?",
            "answer": "No problem. The platform will use your business description and competitor information to generate keywords instead."
          },
          {
            "id": "module-7-sub-2-faq-3",
            "question": "What if my website is brand new or still being built?",
            "answer": "The platform skips your website and builds keywords purely from what you told us during onboarding."
          },
          {
            "id": "module-7-sub-2-faq-4",
            "question": "Does the platform look at my competitors?",
            "answer": "Yes. It scans competitor websites to find keyword ideas you may have missed and adds the best ones to your list."
          },
          {
            "id": "module-7-sub-2-faq-5",
            "question": "What is the Audit Trail in the Keyword Planner?",
            "answer": "The Audit Trail shows you exactly how and why each keyword was recommended to you — which onboarding inputs triggered it, what competitor signals were used, and what logic the platform followed. Full transparency on every keyword suggestion."
          },
          {
            "id": "module-7-sub-2-faq-6",
            "question": "Why does the platform show me how it picked my keywords?",
            "answer": "So you can trust the recommendations. You can see the reasoning behind each keyword — whether it came from your business description, your website, your location, or a competitor gap."
          },
          {
            "id": "module-7-sub-2-faq-7",
            "question": "Do I need Google Analytics or Search Console connected to get keywords?",
            "answer": "No. Keywords are generated using Google Keyword Planner (GKP) as the main data source. GA and GSC are not required to start."
          },
          {
            "id": "module-7-sub-2-faq-8",
            "question": "What happens if I don't have GA or GSC connected?",
            "answer": "The platform treats your brand as new and builds your keyword universe entirely from Google Keyword Planner data, your business inputs, and competitor signals — giving you a complete starting point without any historical data."
          },
          {
            "id": "module-7-sub-2-faq-9",
            "question": "What changes once I connect GA and GSC?",
            "answer": "Your keyword view gets enriched with real performance data — actual search volume, your current ranking position for each keyword, click data, and impression data. This helps you see which keywords you are already winning and where the biggest opportunities are."
          },
          {
            "id": "module-7-sub-2-faq-10",
            "question": "What does the keyword dashboard show before GA/GSC is connected?",
            "answer": "Keyword suggestions with volume estimates and difficulty scores from Google Keyword Planner — enough to build a strong strategy and start optimising immediately."
          },
          {
            "id": "module-7-sub-2-faq-11",
            "question": "What does the keyword dashboard show after GA/GSC is connected?",
            "answer": "Real search volume, your actual keyword rankings, impressions, clicks, and performance trends — all updated from live Google data."
          },
          {
            "id": "module-7-sub-2-faq-12",
            "question": "So can a brand with zero online presence still use the Keyword Planner?",
            "answer": "Yes. The platform assumes every new brand starts from zero. No website, no rankings, no history needed — the system builds everything from scratch using your business details and market data."
          }
        ]
      },
      {
        "id": "module-7-sub-3",
        "subModuleTitle": "How the Keyword List Is Built",
        "faqs": [
          {
            "id": "module-7-sub-3-faq-1",
            "question": "What is the first step in building my keyword list?",
            "answer": "The platform reads your business details — your description, products, services, and website — and picks the most relevant core phrases that represent what your business does. These become the foundation of your entire keyword strategy."
          },
          {
            "id": "module-7-sub-3-faq-2",
            "question": "How does the platform decide which core phrases to pick?",
            "answer": "It scans your business inputs and scores each phrase based on how closely it matches your actual products or services. The most relevant ones are selected automatically — you don't need to choose or type anything."
          },
          {
            "id": "module-7-sub-3-faq-3",
            "question": "Are brand names or words like 'best' and 'cheap' included?",
            "answer": "No. Your brand name, competitor brand names, and vague words like 'best', 'cheap', or 'near me' are filtered out automatically — these don't help the platform build a meaningful keyword strategy."
          },
          {
            "id": "module-7-sub-3-faq-4",
            "question": "What happens after the core phrases are identified?",
            "answer": "The platform uses them to search Google's keyword database and discovers a wider set of related keyword ideas for your business category and location."
          },
          {
            "id": "module-7-sub-3-faq-5",
            "question": "How many keywords will I get?",
            "answer": "It depends on your business. The platform recommends the most relevant keywords based on your specific business details, market demand, and competition — not a fixed number. Quality and relevance always come first."
          },
          {
            "id": "module-7-sub-3-faq-6",
            "question": "How many keywords can I select and lock in?",
            "answer": "You can select and lock a maximum of 20 keywords. These become your active keyword universe that drives your SEO, GMB, and content strategy across the platform."
          },
          {
            "id": "module-7-sub-3-faq-7",
            "question": "Why is there a limit of 20 keywords?",
            "answer": "Focusing on 20 well-chosen keywords gives your business a much better chance of ranking than spreading effort across hundreds. Each selected keyword represents a cluster of related searches — so 20 keywords can actually deliver visibility for far more search terms."
          },
          {
            "id": "module-7-sub-3-faq-8",
            "question": "Are all discovered keywords shown to me?",
            "answer": "No. The platform filters out keywords with very low search demand, keywords unrelated to your business, and keywords that are unrealistic to target. Only relevant, actionable keywords are shown."
          },
          {
            "id": "module-7-sub-3-faq-9",
            "question": "What kinds of keywords are removed before I see the list?",
            "answer": "Keywords with almost no monthly searches, overly niche or confusing phrases, and anything that does not align with your business category or location are removed automatically."
          },
          {
            "id": "module-7-sub-3-faq-10",
            "question": "Can I change my keywords immediately after selecting them?",
            "answer": "No. Once you lock your 20 keywords, they are fixed for 90 days (3 months). This gives the platform enough time to build rankings, measure performance, and deliver meaningful results."
          },
          {
            "id": "module-7-sub-3-faq-11",
            "question": "Why are keywords locked for 90 days?",
            "answer": "SEO and local visibility take time. Changing keywords too frequently resets all progress. A 90-day window gives the platform a fair chance to improve your rankings before any strategy changes are made."
          },
          {
            "id": "module-7-sub-3-faq-12",
            "question": "What happens during the 90-day lock period?",
            "answer": "The platform actively works on your selected keywords — optimising your content, GMB profile, and SEO strategy around them. You can track performance but cannot change the keyword selection."
          },
          {
            "id": "module-7-sub-3-faq-13",
            "question": "Can I see how my locked keywords are performing during the 90 days?",
            "answer": "Yes. The Optimization layer shows keyword ranking movement, impressions, clicks, and performance trends throughout the lock period so you always know how your strategy is progressing."
          },
          {
            "id": "module-7-sub-3-faq-14",
            "question": "What happens after 90 days?",
            "answer": "Your keyword universe unlocks automatically. The platform reviews your performance data and suggests whether to keep, swap, or add keywords based on what worked and what didn't."
          },
          {
            "id": "module-7-sub-3-faq-15",
            "question": "What if my business changes significantly during the 90-day lock?",
            "answer": "Contact the support team via Kiki or raise a ticket. Exceptional changes (new service launch, location change, major pivot) may warrant an early review."
          }
        ]
      },
      {
        "id": "module-7-sub-4",
        "subModuleTitle": "How Keywords Are Scored and Prioritized",
        "faqs": [
          {
            "id": "module-7-sub-4-faq-1",
            "question": "How does the platform decide which keywords are most important?",
            "answer": "It scores each keyword based on how much real traffic it can bring, how well it matches your business, whether it's growing in popularity, and how hard it is to rank for."
          },
          {
            "id": "module-7-sub-4-faq-2",
            "question": "What does 'Business Potential' mean?",
            "answer": "It means how well a keyword matches what you actually sell. If someone searching that word would likely buy from you, it scores high. If the connection is weak, it scores low and gets removed."
          },
          {
            "id": "module-7-sub-4-faq-3",
            "question": "What does 'Traffic Potential' mean?",
            "answer": "It's the estimated number of visitors you could actually get if you ranked well for that keyword — not just how many people search for it in general."
          },
          {
            "id": "module-7-sub-4-faq-4",
            "question": "What does 'Keyword Difficulty' mean?",
            "answer": "It shows how hard it would be to appear on the first page of Google for that keyword. The platform balances difficulty with opportunity so you get realistic targets."
          },
          {
            "id": "module-7-sub-4-faq-5",
            "question": "Are trending keywords given more importance?",
            "answer": "Yes. If a keyword has been getting more searches over the last 6 months, it is prioritized. Keywords that are losing popularity are deprioritized."
          },
          {
            "id": "module-7-sub-4-faq-6",
            "question": "If I already rank on Google Page 1 for a keyword, is it still shown as a priority?",
            "answer": "No. Keywords you already rank for on Page 1 are marked as 'Maintain' — meaning they are already working. The platform focuses your attention on better opportunities."
          },
          {
            "id": "module-7-sub-4-faq-7",
            "question": "Which keywords are the biggest opportunity?",
            "answer": "Keywords where you are currently ranking on Page 2 of Google (positions 11–20). A small improvement can quickly move them to Page 1 and bring a lot more visitors."
          }
        ]
      },
      {
        "id": "module-7-sub-5",
        "subModuleTitle": "Keyword Groups",
        "faqs": [
          {
            "id": "module-7-sub-5-faq-1",
            "question": "What is a keyword group?",
            "answer": "group of related keywords that all point to the same topic or page on your website — for example, 'men's kurta', 'cotton kurta for men', and 'casual kurta' might all be one group."
          },
          {
            "id": "module-7-sub-5-faq-2",
            "question": "Why are keywords grouped?",
            "answer": "So you don't create ten separate pages for the same topic. One strong page covering a group of related keywords performs much better than many thin, competing pages."
          },
          {
            "id": "module-7-sub-5-faq-3",
            "question": "How many keyword groups will I get?",
            "answer": "Between 10 and 15 groups, covering the main topics relevant to your business."
          }
        ]
      },
      {
        "id": "module-7-sub-6",
        "subModuleTitle": "Competitor Keywords",
        "faqs": [
          {
            "id": "module-7-sub-6-faq-1",
            "question": "Does the platform find keywords I'm missing that my competitors use?",
            "answer": "Yes. It checks Google search results for your main keywords, finds the competitor websites that keep appearing, and identifies keyword topics they rank for that you don't have yet."
          },
          {
            "id": "module-7-sub-6-faq-2",
            "question": "Are listing sites like JustDial or Quora counted as competitors?",
            "answer": "No. These are automatically excluded. Only real business competitors similar to yours are analyzed."
          },
          {
            "id": "module-7-sub-6-faq-3",
            "question": "How many extra keywords can I get from competitor analysis?",
            "answer": "Up to 20 additional keyword ideas are suggested from competitor gaps, each verified to have real search volume before being added to your list."
          }
        ]
      },
      {
        "id": "module-7-sub-7",
        "subModuleTitle": "Local vs Online Keywords",
        "faqs": [
          {
            "id": "module-7-sub-7-faq-1",
            "question": "How does the platform know if a keyword should target local customers or online customers?",
            "answer": "It looks at the keyword's meaning and intent. 'Salon near me' clearly targets local customers. 'Buy kurta online' targets online shoppers. Each keyword is automatically assigned to the right channel."
          },
          {
            "id": "module-7-sub-7-faq-2",
            "question": "What happens to local keywords?",
            "answer": "They are sent to GMB Manager to improve your visibility on Google Maps and local search results."
          },
          {
            "id": "module-7-sub-7-faq-3",
            "question": "What happens to online (non-local) keywords?",
            "answer": "They are sent to SEO Manager to improve your website's ranking in Google search results."
          },
          {
            "id": "module-7-sub-7-faq-4",
            "question": "Can I see why a keyword was assigned to GMB or SEO?",
            "answer": "Yes. Every keyword has a short explanation showing the reason it was routed to a specific module — visible when you click or hover on the keyword."
          }
        ]
      },
      {
        "id": "module-7-sub-8",
        "subModuleTitle": "New Businesses",
        "faqs": [
          {
            "id": "module-7-sub-8-faq-1",
            "question": "I just started my business — will the keyword planner still work for me?",
            "answer": "Yes. The platform is built to handle new businesses with no search history. It relies on your onboarding details and competitor research to generate a relevant keyword list."
          },
          {
            "id": "module-7-sub-8-faq-2",
            "question": "Will I get the same keywords as an established business?",
            "answer": "Not exactly. For new businesses, the platform filters out extremely competitive keywords that would be unrealistic to rank for right away, and focuses on more achievable opportunities first."
          },
          {
            "id": "module-7-sub-8-faq-3",
            "question": "Can I update my keywords later as my business grows?",
            "answer": "Yes. Your keyword universe can be refreshed as your business evolves and as new opportunities are identified."
          }
        ]
      }
    ]
  },
  {
    "id": "module-8",
    "moduleTitle": "Ad-Hoc Content Creator",
    "subModules": [
      {
        "id": "module-8-sub-0",
        "subModuleTitle": "Overview & Core Philosophy",
        "faqs": [
          {
            "id": "module-8-sub-0-faq-1",
            "question": "What is the Ad-Hoc Content Creator?",
            "answer": "A DIY content creation platform embedded in Marketing 4SIGHT that combines AI-powered generation, brand-aware personalisation, and multi-format asset creation in a single workspace — without needing designers, writers, video editors, or external tools."
          },
          {
            "id": "module-8-sub-0-faq-2",
            "question": "How is it different from tools like Canva?",
            "answer": "Canva provides templates from its library; the Ad-Hoc Content Creator generates a first draft meeting your specific brief, then lets you edit it. The AI acts as an assistant — you own and control the final output."
          },
          {
            "id": "module-8-sub-0-faq-3",
            "question": "Does the AI replace the user?",
            "answer": "No. The AI generates the first draft. Every AI-generated element remains editable. The canvas editor is the primary workspace, and the user owns the final output."
          },
          {
            "id": "module-8-sub-0-faq-4",
            "question": "Which platforms and formats does it support?",
            "answer": "Social media graphics (static, infographic, carousel), videos and GIFs, LinkedIn articles, blog posts, white papers, case studies, landing pages, letterheads, business cards, flyers, brochures, standees, tent cards, banners, website banners, social media cover images, presentation slide decks, and product/service catalogues."
          }
        ]
      },
      {
        "id": "module-8-sub-1",
        "subModuleTitle": "User Types",
        "faqs": [
          {
            "id": "module-8-sub-1-faq-1",
            "question": "What is an Existing Marketing 4SIGHT User?",
            "answer": "An existing client whose brand profile (name, logo, colours, typography, products, services, audience, brand voice, content history) is already in the system. No onboarding questions are asked — brand context is applied automatically to every asset."
          },
          {
            "id": "module-8-sub-1-faq-2",
            "question": "What is a Guest User?",
            "answer": "A new user with no prior profile. They register with Full Name, Email, and Mobile Number, verify their email, then log in. Only the Ad-Hoc Content Creator is unlocked — all other modules remain locked until they upgrade."
          },
          {
            "id": "module-8-sub-1-faq-3",
            "question": "What information does a Guest User need to provide?",
            "answer": "Minimal brand context: Brand Name, Company Description, Website URL, Logo Upload, and Industry — all optional. If a website URL is provided, the system auto-extracts brand information."
          }
        ]
      },
      {
        "id": "module-8-sub-2",
        "subModuleTitle": "Brand Intelligence Engine",
        "faqs": [
          {
            "id": "module-8-sub-2-faq-1",
            "question": "What is the Brand Intelligence Engine?",
            "answer": "A centralised brand profile layer that every content generation request passes through. It stores and applies brand identity, visual guidelines, marketing context, and communication style automatically."
          },
          {
            "id": "module-8-sub-2-faq-2",
            "question": "What does the system extract if I provide a Website URL?",
            "answer": "Brand Name, Logo, Primary and Secondary Colours, Typography, Products, Services, Meta Description, Business Category, and Contact Information — shown for user approval before use."
          },
          {
            "id": "module-8-sub-2-faq-3",
            "question": "Does the system learn from my usage?",
            "answer": "Yes. After each export, the system analyses colour usage, typography, layout preferences, and frequently used assets, and updates the brand profile automatically."
          }
        ]
      },
      {
        "id": "module-8-sub-3",
        "subModuleTitle": "Content Discovery Screen & Content Types",
        "faqs": [
          {
            "id": "module-8-sub-3-faq-1",
            "question": "What is the Content Discovery Screen?",
            "answer": "The central entry point into content creation, displayed on the dashboard with the heading 'What would you like to create today?' — showing all available content types as visual cards."
          },
          {
            "id": "module-8-sub-3-faq-2",
            "question": "What content categories are available?",
            "answer": "Six categories: Social Media Graphics (static post, infographic, carousel), Video & Motion (short-form video, social reel, animated GIF), Professional Writing (LinkedIn article, blog post, white paper, case study, landing page content), Corporate Print Collateral (letterhead, business card, flyer, brochure), Physical Signage & Display (standee, tent card, banner), Digital Web Assets (website banner, social media cover image), and Business Communications (presentation slide deck, product/service catalogue)."
          },
          {
            "id": "module-8-sub-3-faq-3",
            "question": "What happens when I select a content type?",
            "answer": "The system creates a draft workspace with the correct canvas dimensions, design rules, export options, prompt requirements, and AI generation logic for that specific content type."
          }
        ]
      },
      {
        "id": "module-8-sub-4",
        "subModuleTitle": "Social Media Static Post",
        "faqs": [
          {
            "id": "module-8-sub-4-faq-1",
            "question": "Which platforms are supported for static posts?",
            "answer": "Instagram, Facebook, LinkedIn, and WhatsApp."
          },
          {
            "id": "module-8-sub-4-faq-2",
            "question": "What asset dimensions are available?",
            "answer": "Feed Portrait (1080×1350 / 4:5), Feed Square (1080×1080 / 1:1), Feed Landscape (1080×566 or 1200×630 depending on platform), Story (1080×1920 / 9:16), WhatsApp Status (1080×1920), and WhatsApp Promotional Forward (1080×1080)."
          },
          {
            "id": "module-8-sub-4-faq-3",
            "question": "What inputs are required to generate a static post?",
            "answer": "Mandatory: Content Topic & Brief, and Call to Action (CTA). Optional: Content Objective, Product/Service details, Target Audience, Reference Content (PDF/DOCX/PPTX/TXT/URL), and Reference Image (JPG/PNG/WEBP)."
          },
          {
            "id": "module-8-sub-4-faq-4",
            "question": "What does the AI generate?",
            "answer": "A fully composed creative with Headline, Supporting Text, CTA, Images, Icons, Logo, Brand Colours, and Layout Structure — all editable inside the Visual Grid Canvas."
          },
          {
            "id": "module-8-sub-4-faq-5",
            "question": "What can I edit after generation?",
            "answer": "Text, typography, colours, images, logo, layout, icons, and AI refinement commands (e.g., 'Make the headline shorter', 'Make it suitable for LinkedIn')."
          },
          {
            "id": "module-8-sub-4-faq-6",
            "question": "What export formats are available?",
            "answer": "PNG, JPG, and PDF."
          }
        ]
      },
      {
        "id": "module-8-sub-5",
        "subModuleTitle": "Social Media Infographic",
        "faqs": [
          {
            "id": "module-8-sub-5-faq-1",
            "question": "What is the Infographic module for?",
            "answer": "Creating visually structured information-based creatives — process flows, timelines, comparisons, statistics, checklists, feature breakdowns, research summaries, and more."
          },
          {
            "id": "module-8-sub-5-faq-2",
            "question": "What infographic types are supported?",
            "answer": "Process Flow, Timeline, Comparison, Statistics, Checklist, Educational Guide, Feature Breakdown, Benefits Overview, and Custom (free text)."
          },
          {
            "id": "module-8-sub-5-faq-3",
            "question": "What inputs are mandatory?",
            "answer": "Infographic Topic & Brief, and Infographic Type. Optional: Product/Service, Reference Content, Target Audience, CTA, and Reference Images."
          },
          {
            "id": "module-8-sub-5-faq-4",
            "question": "What export formats are available?",
            "answer": "PNG, JPG, and PDF."
          }
        ]
      },
      {
        "id": "module-8-sub-6",
        "subModuleTitle": "Social Media Carousel",
        "faqs": [
          {
            "id": "module-8-sub-6-faq-1",
            "question": "What is the Carousel module?",
            "answer": "A multi-slide content format where information is distributed across 2–6 connected slides with a uniform design language, used for storytelling, education, product showcases, and thought leadership."
          },
          {
            "id": "module-8-sub-6-faq-2",
            "question": "How many slides can a carousel have?",
            "answer": "Minimum 2, maximum 6. Default is 6. Users can add, duplicate, or remove slides."
          },
          {
            "id": "module-8-sub-6-faq-3",
            "question": "Which platforms support carousels?",
            "answer": "Instagram, Facebook, and LinkedIn."
          },
          {
            "id": "module-8-sub-6-faq-4",
            "question": "What does the AI generate for a carousel?",
            "answer": "A complete storyline structure (e.g., Slide 1: Hook, Slide 2: Problem, Slide 3: Insight, Slide 4: Solution, Slide 5: Benefits, Slide 6: CTA) with slide-wise content and consistent branding."
          },
          {
            "id": "module-8-sub-6-faq-5",
            "question": "What export formats are available?",
            "answer": "PNG (individual slides), JPG (individual slides), PDF (entire carousel), and ZIP (all slides combined)."
          }
        ]
      },
      {
        "id": "module-8-sub-7",
        "subModuleTitle": "Social Media Video & Motion",
        "faqs": [
          {
            "id": "module-8-sub-7-faq-1",
            "question": "What video formats are supported?",
            "answer": "Short-Form Video (up to 90 seconds), Social Reel/Video (up to 60 seconds), and Animated GIF (up to 15 seconds)."
          },
          {
            "id": "module-8-sub-7-faq-2",
            "question": "Which platforms are supported for video?",
            "answer": "Instagram (Reel, Story), Facebook (Reel, Feed Video, Story), LinkedIn (Feed Video), WhatsApp (Status Video), and YouTube (Shorts)."
          },
          {
            "id": "module-8-sub-7-faq-3",
            "question": "How does video generation work — is it a single step?",
            "answer": "No. It follows a two-stage workflow: Stage 1 generates a Storyboard & Script for user approval; Stage 2 generates the actual video only after the user clicks 'Approve & Generate Video'. This controls costs and gives the user full control over messaging before rendering."
          },
          {
            "id": "module-8-sub-7-faq-4",
            "question": "What does each storyboard scene card contain?",
            "answer": "Scene Number, Scene Objective, Visual Direction, On-Screen Text, Voiceover Script, Estimated Duration, Transition Type, and Background Asset Source."
          },
          {
            "id": "module-8-sub-7-faq-5",
            "question": "What can I edit before approving the storyboard?",
            "answer": "Script (add/remove/rewrite), Scene Order, Scene Duration, Visual Direction, Voiceover, CTA, and AI refinement commands."
          },
          {
            "id": "module-8-sub-7-faq-6",
            "question": "What can I edit after the video is generated?",
            "answer": "Only minor edits: text corrections, logo replacement, audio replacement, CTA edits, scene deletion, scene reordering, and brand colour adjustments. Major changes require returning to the storyboard."
          },
          {
            "id": "module-8-sub-7-faq-7",
            "question": "What voiceover options are available?",
            "answer": "No Voiceover, AI Voiceover, or User Upload Voiceover."
          },
          {
            "id": "module-8-sub-7-faq-8",
            "question": "What export formats are supported for video?",
            "answer": "MP4 and MOV for videos; GIF and MP4 for Animated GIFs. Resolutions: 720p and 1080p."
          },
          {
            "id": "module-8-sub-7-faq-9",
            "question": "What is the Asset Source Priority order for videos?",
            "answer": "Priority 1: User Uploaded Assets. Priority 2: Brand Asset Library. Priority 3: Licensed Stock Assets. Priority 4: AI Generated Assets."
          }
        ]
      },
      {
        "id": "module-8-sub-8",
        "subModuleTitle": "LinkedIn Article",
        "faqs": [
          {
            "id": "module-8-sub-8-faq-1",
            "question": "What is the LinkedIn Article module for?",
            "answer": "Generating long-form thought leadership content optimised for LinkedIn publishing — educational, insight-driven, and narrative-focused to build authority and professional credibility."
          },
          {
            "id": "module-8-sub-8-faq-2",
            "question": "What inputs are mandatory?",
            "answer": "Topic/Subject & Brief, Key Message, and Author Name. Optional: Article Objective, Target Audience, Tone of Voice, Reference Documents, and Reference URLs."
          },
          {
            "id": "module-8-sub-8-faq-3",
            "question": "What is the default article length?",
            "answer": "800–1200 words."
          },
          {
            "id": "module-8-sub-8-faq-4",
            "question": "Does the output include a cover image?",
            "answer": "Yes. Every LinkedIn Article automatically includes a generated cover image as part of the first draft, editable inside the Visual Grid Canvas. It can also be exported separately."
          },
          {
            "id": "module-8-sub-8-faq-5",
            "question": "What export formats are available?",
            "answer": "DOCX, PDF, TXT, and HTML."
          }
        ]
      },
      {
        "id": "module-8-sub-9",
        "subModuleTitle": "Blog Post",
        "faqs": [
          {
            "id": "module-8-sub-9-faq-1",
            "question": "What makes the Blog Post module different from the LinkedIn Article?",
            "answer": "Blog Posts prioritise SEO — search visibility, keyword optimisation, organic traffic, and search intent alignment. LinkedIn Articles are optimised for professional narrative and thought leadership."
          },
          {
            "id": "module-8-sub-9-faq-2",
            "question": "What inputs are mandatory?",
            "answer": "Primary Keyword and Blog Topic & Brief. Optional: Secondary Keywords, Search Intent, Target Audience, Objective, Products/Services, CTA, Reference Documents, and Reference URLs."
          },
          {
            "id": "module-8-sub-9-faq-3",
            "question": "What SEO elements are auto-generated?",
            "answer": "Meta Title, Meta Description, URL Slug, Focus Keyword, Secondary Keywords, H1, H2 structure, FAQ Section, Suggested Internal/External Links, Recommended Images, and Suggested ALT Text."
          },
          {
            "id": "module-8-sub-9-faq-4",
            "question": "What SEO quality indicators does the workspace show?",
            "answer": "SEO Score (keyword usage, readability, heading structure, content length, internal linking, meta optimisation), Readability Score, and Keyword Coverage (primary, secondary, density)."
          },
          {
            "id": "module-8-sub-9-faq-5",
            "question": "What is the default blog post length?",
            "answer": "1000–1500 words."
          },
          {
            "id": "module-8-sub-9-faq-6",
            "question": "What export formats are available?",
            "answer": "DOCX, PDF, TXT, and HTML."
          }
        ]
      },
      {
        "id": "module-8-sub-10",
        "subModuleTitle": "White Paper",
        "faqs": [
          {
            "id": "module-8-sub-10-faq-1",
            "question": "What is the White Paper module for?",
            "answer": "Creating long-form authoritative documents for lead generation, thought leadership, enterprise sales enablement, research publications, product education, and market analysis."
          },
          {
            "id": "module-8-sub-10-faq-2",
            "question": "What white paper types are supported?",
            "answer": "Research, Industry, Technology, Product, Solution, Market Analysis, Strategic Framework, and Custom White Paper."
          },
          {
            "id": "module-8-sub-10-faq-3",
            "question": "What inputs are mandatory?",
            "answer": "White Paper Topic & Brief, Business Objective, Target Audience, Problem Statement, and Number of Pages."
          },
          {
            "id": "module-8-sub-10-faq-4",
            "question": "What is the default page range?",
            "answer": "4–6 pages. Users can select a higher page count."
          },
          {
            "id": "module-8-sub-10-faq-5",
            "question": "What does the AI auto-generate for the white paper structure?",
            "answer": "Cover Page, Executive Summary, Introduction, Problem Statement, Industry Analysis, Findings, Solution Framework, Recommendations, Conclusion, About The Company, and Contact Information."
          },
          {
            "id": "module-8-sub-10-faq-6",
            "question": "Can supporting visuals be generated?",
            "answer": "Yes — Data Charts, Process Diagrams, Framework Visuals, Comparison Tables, Timelines, and Infographics where applicable."
          },
          {
            "id": "module-8-sub-10-faq-7",
            "question": "What export formats are available?",
            "answer": "PDF, DOCX, PPTX, HTML, Print-Ready PDF (CMYK), and an optional ZIP Asset Package (PDF + Cover Image + Supporting Graphics)."
          }
        ]
      },
      {
        "id": "module-8-sub-11",
        "subModuleTitle": "Case Study",
        "faqs": [
          {
            "id": "module-8-sub-11-faq-1",
            "question": "What is the Case Study module for?",
            "answer": "Creating structured business success stories that demonstrate how a product, service, or initiative solved a specific challenge and delivered measurable outcomes — for sales enablement, proposals, lead generation, and client presentations."
          },
          {
            "id": "module-8-sub-11-faq-2",
            "question": "What inputs are mandatory?",
            "answer": "Case Study Title & Brief, Project/Engagement Description, Business Challenge, Solution Provided, Key Deliverables, Results Achieved, Quantitative Metrics, and Before vs After Information."
          },
          {
            "id": "module-8-sub-11-faq-3",
            "question": "What if I don't want to name the client?",
            "answer": "Client name is optional. The system uses generic references like 'Client', 'Leading Manufacturing Company', or 'Global Technology Firm' instead."
          },
          {
            "id": "module-8-sub-11-faq-4",
            "question": "What does the AI auto-generate?",
            "answer": "Cover Page, Executive Summary, About the Client, Business Challenge, Solution Implemented, Implementation Process, Results & Outcomes, Before vs After Comparison, Client Testimonial (if provided), Key Takeaways, and CTA."
          },
          {
            "id": "module-8-sub-11-faq-5",
            "question": "What export formats are available?",
            "answer": "PDF, DOCX, PPTX, HTML, and Shareable Link."
          }
        ]
      },
      {
        "id": "module-8-sub-12",
        "subModuleTitle": "Landing Page Content",
        "faqs": [
          {
            "id": "module-8-sub-12-faq-1",
            "question": "What is the Landing Page Content module for?",
            "answer": "Generating complete, conversion-focused landing pages for campaigns, product launches, lead generation, event registrations, and service promotions — without designers or developers."
          },
          {
            "id": "module-8-sub-12-faq-2",
            "question": "What landing page types are supported?",
            "answer": "Lead Generation, Product, Service, Event Registration, Webinar Registration, Consultation Booking, Course Admission, Download, and Custom Landing Page."
          },
          {
            "id": "module-8-sub-12-faq-3",
            "question": "What inputs are mandatory?",
            "answer": "Landing Page Objective/Type, Product/Service Name, Product/Service Description, Target Audience, Primary CTA, Key Benefits, and Features."
          },
          {
            "id": "module-8-sub-12-faq-4",
            "question": "Does the system generate HTML code?",
            "answer": "Yes. The system generates deployment-ready Semantic HTML5, Responsive CSS, CTA buttons, forms, images, and typography — ready to upload directly to a hosting server or hand off to a developer."
          },
          {
            "id": "module-8-sub-12-faq-5",
            "question": "What SEO elements are auto-generated?",
            "answer": "Meta Title, Meta Description, Open Graph Title and Description, Structured Data Schema (Organization, Product, Service, FAQ, Event), and URL Slug Recommendation."
          },
          {
            "id": "module-8-sub-12-faq-6",
            "question": "What preview modes are available?",
            "answer": "Desktop (1920px), Tablet (768px), and Mobile (375px) — switchable in real time."
          },
          {
            "id": "module-8-sub-12-faq-7",
            "question": "What export formats are available?",
            "answer": "DOCX, PDF, HTML, HTML+CSS ZIP Package (with index.html, style.css, image assets, favicon), and a Shareable Preview Link."
          }
        ]
      },
      {
        "id": "module-8-sub-13",
        "subModuleTitle": "Letterhead",
        "faqs": [
          {
            "id": "module-8-sub-13-faq-1",
            "question": "What does the Letterhead module generate?",
            "answer": "A professionally designed, brand-compliant letterhead template only — the header, body area (intentionally blank), and footer. It does not generate offer letters, appointment letters, or any letter content."
          },
          {
            "id": "module-8-sub-13-faq-2",
            "question": "What inputs are mandatory?",
            "answer": "Company Name and Brand Colours. All other inputs (Logo, Address, Contact Information, Social Handles, Preferred Style, Watermark) are optional — missing fields are shown as editable placeholders."
          },
          {
            "id": "module-8-sub-13-faq-3",
            "question": "What page sizes are supported?",
            "answer": "A4 and Letter Size. Output is CMYK-compatible and print-safe."
          },
          {
            "id": "module-8-sub-13-faq-4",
            "question": "What export formats are available?",
            "answer": "DOCX, PPTX, PDF (Print Ready), PNG, and JPG."
          }
        ]
      },
      {
        "id": "module-8-sub-14",
        "subModuleTitle": "Business Card",
        "faqs": [
          {
            "id": "module-8-sub-14-faq-1",
            "question": "What does the Business Card module generate?",
            "answer": "A complete front and back business card design. All inputs are optional — missing fields are replaced with editable placeholders."
          },
          {
            "id": "module-8-sub-14-faq-2",
            "question": "What inputs can I provide?",
            "answer": "Full Name, Designation, Company Name, Logo, Mobile Number, Email, Website URL, Office Address, Social Media Handles, QR Code Destination, Brand Colours, and Preferred Design Style."
          },
          {
            "id": "module-8-sub-14-faq-3",
            "question": "Can I add a QR code?",
            "answer": "Yes. Provide a QR Code Destination (website, LinkedIn, WhatsApp, vCard, etc.) and the system auto-generates and places a scannable QR code on the card."
          },
          {
            "id": "module-8-sub-14-faq-4",
            "question": "What print sizes are supported?",
            "answer": "3.5×2 inches (US Standard) and 85×55mm (International Standard). Output is 300 DPI, CMYK, with bleed area support."
          },
          {
            "id": "module-8-sub-14-faq-5",
            "question": "What export formats are available?",
            "answer": "PDF (Print Ready), PNG, JPG, and PPTX."
          }
        ]
      },
      {
        "id": "module-8-sub-15",
        "subModuleTitle": "Leaflet / Flyer",
        "faqs": [
          {
            "id": "module-8-sub-15-faq-1",
            "question": "What flyer sizes are supported?",
            "answer": "A6 (105×148mm), A5 (148×210mm), A4 (210×297mm), DL (99×210mm), Square (210×210mm), and Custom Size. Orientation: Portrait or Landscape."
          },
          {
            "id": "module-8-sub-15-faq-2",
            "question": "What inputs are mandatory?",
            "answer": "Flyer Objective and Flyer Title/Headline. Optional: Supporting Content, CTA, Contact Information, Images, and Logo."
          },
          {
            "id": "module-8-sub-15-faq-3",
            "question": "Is the workspace print-ready?",
            "answer": "Yes. The workspace operates in CMYK mode by default and automatically displays Safe Margins, Trim Area, Bleed Area, and Print Boundaries."
          },
          {
            "id": "module-8-sub-15-faq-4",
            "question": "What export formats are available?",
            "answer": "PDF (Print Ready CMYK), PDF with Bleed, PDF without Bleed, PNG, and JPG."
          }
        ]
      },
      {
        "id": "module-8-sub-16",
        "subModuleTitle": "Brochure",
        "faqs": [
          {
            "id": "module-8-sub-16-faq-1",
            "question": "How many pages can a brochure have?",
            "answer": "Minimum 6 pages, maximum 16 pages. Front Cover and Back Cover are always mandatory."
          },
          {
            "id": "module-8-sub-16-faq-2",
            "question": "What sizes are supported?",
            "answer": "A4 Portrait (210×297mm), A4 Landscape (297×210mm), A5 Portrait (148×210mm), and Square (210×210mm). Default is A4 Portrait."
          },
          {
            "id": "module-8-sub-16-faq-3",
            "question": "What inputs are mandatory?",
            "answer": "Brochure Topic, Brochure Objective, and Key Sections to Include. Optional: Product/Service Details, Target Audience, Existing Company Material, Images, CTA, and QR Code Destination."
          },
          {
            "id": "module-8-sub-16-faq-4",
            "question": "What print standards are enforced?",
            "answer": "CMYK colour mode, 3mm bleed on all sides, 5mm safe margin, and minimum 300 DPI image quality. A pre-export print validation check flags missing images, low-res images, overflowing text, missing logos, and margin violations."
          },
          {
            "id": "module-8-sub-16-faq-5",
            "question": "What export formats are available?",
            "answer": "PDF (Print Ready CMYK), PDF (Standard), PPTX, PNG (page-wise), JPG (page-wise), and ZIP Package (PDF + images + uploaded assets)."
          }
        ]
      },
      {
        "id": "module-8-sub-17",
        "subModuleTitle": "Standee",
        "faqs": [
          {
            "id": "module-8-sub-17-faq-1",
            "question": "What sizes are supported for standees?",
            "answer": "2ft×5ft, 2.5ft×6ft, 3ft×6ft (default), and 4ft×6ft."
          },
          {
            "id": "module-8-sub-17-faq-2",
            "question": "What inputs are mandatory?",
            "answer": "Standee Topic, Standee Objective, and Primary Call to Action. Optional: Product/Service Information, Target Audience, Contact Information, QR Code URL, Images, and Standee Content (if user has ready copy)."
          },
          {
            "id": "module-8-sub-17-faq-3",
            "question": "What print requirements are enforced for standees?",
            "answer": "CMYK, 5mm bleed, 10mm safe margin, 300 DPI, and distance readability validation (headline size, CTA visibility, QR scan size, contrast ratio)."
          },
          {
            "id": "module-8-sub-17-faq-4",
            "question": "What export formats are available?",
            "answer": "PDF (Print Ready CMYK), PDF (Standard), PPTX, PNG, JPG, and ZIP Package."
          }
        ]
      },
      {
        "id": "module-8-sub-18",
        "subModuleTitle": "Tent Card",
        "faqs": [
          {
            "id": "module-8-sub-18-faq-1",
            "question": "What tent card types are supported?",
            "answer": "Single-side (front content, back empty) and Double-side (same content or different content on each side)."
          },
          {
            "id": "module-8-sub-18-faq-2",
            "question": "What sizes are supported?",
            "answer": "A6, A5 (default), DL (99×210mm), and Custom size."
          },
          {
            "id": "module-8-sub-18-faq-3",
            "question": "What inputs are mandatory?",
            "answer": "Tent Card Title and Purpose/Objective. For double-sided cards: Front Side Content and Back Side Content are also mandatory."
          },
          {
            "id": "module-8-sub-18-faq-4",
            "question": "Does the system show fold guides?",
            "answer": "Yes. Fold guides appear in the editor but do not appear in the final print output."
          },
          {
            "id": "module-8-sub-18-faq-5",
            "question": "What export formats are available?",
            "answer": "PDF (Print Ready CMYK), PDF (Standard), PPTX, PNG, JPG, and ZIP Package."
          }
        ]
      },
      {
        "id": "module-8-sub-19",
        "subModuleTitle": "Banner",
        "faqs": [
          {
            "id": "module-8-sub-19-faq-1",
            "question": "What banner sizes are supported?",
            "answer": "Multiple sizes from 52cm×91cm up to 76cm×366cm, plus Custom input. No default size — the user must select a size before proceeding."
          },
          {
            "id": "module-8-sub-19-faq-2",
            "question": "What inputs are mandatory?",
            "answer": "Banner Topic, Key Message, and Call to Action (CTA). Optional: Banner Objective, Product/Service Information, Target Audience, Contact Information, QR Code URL, and Images."
          },
          {
            "id": "module-8-sub-19-faq-3",
            "question": "What print and readability requirements are enforced?",
            "answer": "CMYK, 5mm bleed, 10mm safe margin, 300 DPI, and distance readability validation (headline visibility, CTA readability, font size adequacy, contrast compliance)."
          },
          {
            "id": "module-8-sub-19-faq-4",
            "question": "What export formats are available?",
            "answer": "PDF (Print Ready CMYK), PDF (Standard), PPTX, PNG, JPG, TIFF (Print Quality), and ZIP Package."
          }
        ]
      },
      {
        "id": "module-8-sub-20",
        "subModuleTitle": "Website Banner",
        "faqs": [
          {
            "id": "module-8-sub-20-faq-1",
            "question": "What is the Website Banner module for?",
            "answer": "Generating conversion-focused website banners for any page (Homepage, Service, Product, Blog, Landing Page, Campaign, etc.) with responsive previews for Desktop, Tablet, and Mobile."
          },
          {
            "id": "module-8-sub-20-faq-2",
            "question": "How do I specify the banner size?",
            "answer": "You enter Width (px) and Height (px) manually. The system auto-configures canvas size, safe zones, text areas, and responsive scaling based on your dimensions."
          },
          {
            "id": "module-8-sub-20-faq-3",
            "question": "What inputs are mandatory?",
            "answer": "Banner Purpose, Page Type, and Key Message. Optional: Service/Product Name, CTA, Target Audience, Supporting Content, and Reference Images."
          },
          {
            "id": "module-8-sub-20-faq-4",
            "question": "What export formats are available?",
            "answer": "PNG, JPG, WEBP, PDF, and separate Desktop/Tablet/Mobile versions in a Combined ZIP Package."
          }
        ]
      },
      {
        "id": "module-8-sub-21",
        "subModuleTitle": "Social Media Cover Image",
        "faqs": [
          {
            "id": "module-8-sub-21-faq-1",
            "question": "Which platforms are supported for cover images?",
            "answer": "Facebook Business Page, LinkedIn Company Page, LinkedIn Personal Profile, X (Twitter) Profile, YouTube Channel, Facebook Group, Facebook Profile, and LinkedIn Group."
          },
          {
            "id": "module-8-sub-21-faq-2",
            "question": "Does the system handle safe zones automatically?",
            "answer": "Yes. Platform-specific safe zones are auto-applied — for example, LinkedIn cover zones prevent overlap with the profile image, and YouTube channel art safe zones ensure visibility across TV, desktop, tablet, and mobile."
          },
          {
            "id": "module-8-sub-21-faq-3",
            "question": "What inputs are mandatory?",
            "answer": "Headline/Key Message. Optional: Cover Image Purpose, Supporting Message, Products/Services, Target Audience, Website URL, Reference Content, and Reference Images."
          },
          {
            "id": "module-8-sub-21-faq-4",
            "question": "What export formats are available?",
            "answer": "PNG, JPG, WEBP, PDF, Platform Optimised Version, High Resolution Version, and Web Optimised Version."
          }
        ]
      },
      {
        "id": "module-8-sub-22",
        "subModuleTitle": "Presentation Slide Deck",
        "faqs": [
          {
            "id": "module-8-sub-22-faq-1",
            "question": "What types of presentations can be created?",
            "answer": "Company Profile, Sales Deck, Investor Presentation, Product/Service Presentation, Business Proposal, Training Deck, Workshop, Academic, Project, Conference, Webinar, and Event Presentations."
          },
          {
            "id": "module-8-sub-22-faq-2",
            "question": "How many slides can a presentation have?",
            "answer": "Minimum 6, maximum 16. Default is 6. The first slide is always a Cover Slide; the last is always a Thank You/Contact Slide."
          },
          {
            "id": "module-8-sub-22-faq-3",
            "question": "Does the AI generate slides directly?",
            "answer": "No. The system first generates a Presentation Outline (slide number, title, objective, talking points, visual direction) for user approval. Only after approval does full slide generation begin."
          },
          {
            "id": "module-8-sub-22-faq-4",
            "question": "What can I edit in the outline before approving?",
            "answer": "Slide titles, slide sequence, add/delete slides, and regenerate the entire outline."
          },
          {
            "id": "module-8-sub-22-faq-5",
            "question": "What chart types are supported in slides?",
            "answer": "Bar Charts, Pie Charts, Line Charts, and Comparison Charts — with editable data, labels, colours, and titles."
          },
          {
            "id": "module-8-sub-22-faq-6",
            "question": "What export formats are available?",
            "answer": "PPTX (fully editable in PowerPoint and Google Slides), PDF, PNG (per slide), and JPG (per slide) for individual or selected slides."
          }
        ]
      },
      {
        "id": "module-8-sub-23",
        "subModuleTitle": "Product/Service Catalogue",
        "faqs": [
          {
            "id": "module-8-sub-23-faq-1",
            "question": "What types of catalogues can be created?",
            "answer": "Product, Service, Product & Service, Corporate Capability, Training Programme, Educational Course, Real Estate, Hospitality, and Custom Catalogues."
          },
          {
            "id": "module-8-sub-23-faq-2",
            "question": "How many pages can a catalogue have?",
            "answer": "Minimum 6, maximum 20. Default is 6. Front Cover and Back Cover are always mandatory."
          },
          {
            "id": "module-8-sub-23-faq-3",
            "question": "What inputs are mandatory?",
            "answer": "Catalogue Title, Product/Service Information, Product/Service Images, Product/Service Data Upload, and Key Selling Points. Optional: Catalogue Objective, Target Audience, Pricing Information, and Reference Content."
          },
          {
            "id": "module-8-sub-23-faq-4",
            "question": "What data formats can I upload for bulk catalogue generation?",
            "answer": "XLSX, CSV, PDF, DOCX, and PPTX — the AI extracts product names, categories, descriptions, features, pricing, and specifications automatically."
          },
          {
            "id": "module-8-sub-23-faq-5",
            "question": "Is there a structure approval step before full generation?",
            "answer": "Yes. The system presents a proposed page structure (page number, title, objective, content summary, suggested layout) for approval before generating the full catalogue."
          },
          {
            "id": "module-8-sub-23-faq-6",
            "question": "What export formats are available?",
            "answer": "PDF (CMYK Ready), High Resolution PDF, PPTX, DOCX, PNG (per page), JPG (per page), and individual or selected page exports."
          }
        ]
      },
      {
        "id": "module-8-sub-24",
        "subModuleTitle": "Common Features Across All Content Types",
        "faqs": [
          {
            "id": "module-8-sub-24-faq-1",
            "question": "Do all content types support real-time preview?",
            "answer": "Yes. Every canvas functions as a live preview — text, colour, image, font, and layout changes update instantly. No separate preview page is required for any content type."
          },
          {
            "id": "module-8-sub-24-faq-2",
            "question": "Can I give AI refinement commands after generation?",
            "answer": "Yes. Natural language commands (e.g., 'Make the headline shorter', 'Make it more professional', 'Add statistics') are supported across all content types. The AI modifies only the selected element or section — not the entire asset."
          },
          {
            "id": "module-8-sub-24-faq-3",
            "question": "Does the system apply my brand identity automatically?",
            "answer": "Yes. Brand colours, typography, logo placement, and design style are applied automatically for Existing Users. Guest Users have brand assets applied from their uploaded materials or website intelligence data."
          },
          {
            "id": "module-8-sub-24-faq-4",
            "question": "Can I upload reference documents to any content type?",
            "answer": "Yes. Most content types accept PDF, DOCX, PPTX, TXT, and URL references for additional context, facts, or inspiration."
          },
          {
            "id": "module-8-sub-24-faq-5",
            "question": "What happens to my content assets after creation?",
            "answer": "All content assets are saved in your Ad-Hoc Content Creator dashboard under Recently Created Assets, Recent Exports, and Continue Editing — accessible at any time."
          },
          {
            "id": "module-8-sub-24-faq-6",
            "question": "Do print assets use CMYK colour mode?",
            "answer": "Yes. All print-oriented assets (flyer, brochure, standee, tent card, banner, letterhead, business card) operate in CMYK mode by default with bleed areas, safe margins, and print validation built in."
          }
        ]
      }
    ]
  }
];
